// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import scenarioService from "../../services/scenarioService";
import { formatErrorMessage } from "../../utils/apiHelper";
import {
  Square,
  Circle,
  Type,
  Trash2,
  RotateCw,
  Layers,
  Minus,
  Plus,
  Settings,
  Pentagon,
  Maximize2,
  Eye,
  EyeOff,
  FileImage,
  MapPin,
  AlignRight,
  Lock,
  Unlock,
  List,
  Building,
  X,
  Map,
  Loader2,
} from "lucide-react";
import {
  LAYERS,
  LAYER_CONFIG,
  PARK_OBJECTS,
  generateId,
  initializeMissingGroups,
  distance,
  DEFAULT_VIEWBOX,
} from "../../utils/scenarioEditorUtils";
import {
  useGetNearestVertex,
  useGetNearestEdge,
  usePointToSegmentDistance,
  useGetElementBounds,
  useIsPointInBounds,
  useSnapToGridCoords,
  useGetSVGCoordinates,
  usePixelToLngLat,
  useLngLatToPixel,
} from "../../hooks/useScenarioEditorHelpers";
import RightPanel from "../../components/ScenarioEditor/RightPanel";
import Toolbar from "../../components/ScenarioEditor/Toolbar";
import TopControls from "../../components/ScenarioEditor/TopControls";
import {
  ScenarioSettingsModal,
  PasteSVGDialogModal,
  GroupManagerModal,
  CreateGroupModal,
} from "../../components/ScenarioEditor/Modals";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { useSVGImportExport } from "../../hooks/useSVGImportExport";
import { useBackgroundImage } from "../../hooks/useBackgroundImage";
import { useGPSHelpers } from "../../hooks/useGPSHelpers";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import spaceService from "../../services/spaceService";
import useScenarioSpaces, {
  spaceToElement,
  elementToSpacePayload,
  makeSvgToLatLng,
} from "../../hooks/useScenarioSpaces";
import useScenarioConfig from "../../hooks/useScenarioConfig";
import ScenarioPropertiesPanel from "../../components/ScenarioEditor/ScenarioPropertiesPanel";
import SpacePropertiesModal from "../../components/ScenarioEditor/SpacePropertiesModal";

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 12;

const MemoElement = React.memo(function MemoElement({ element, render }) {
  return render(element);
});

export default function ScenarioEditor() {
  const { scenarioId: routeScenarioId } = useParams();
  const navigate = useNavigate();
  const scenarioId = routeScenarioId ? Number(routeScenarioId) : null;
  const {
    spaces,
    loading: spacesLoading,
    persistAll: persistSpaces,
    refetch: refetchSpaces,
  } = useScenarioSpaces(scenarioId);

  const {
    scenario: loadedScenario,
    useCases,
    assignments: loadedAssignments,
    persistAll: persistScenarioConfig,
    loading: configLoading,
  } = useScenarioConfig(scenarioId);

  const isScenarioLoading =
    Boolean(scenarioId) && (spacesLoading || configLoading);
  const [scenarioPatch, setScenarioPatch] = useState(null);
  const [desiredAssignments, setDesiredAssignments] = useState(null);
  const [hasSeededSpaces, setHasSeededSpaces] = useState(false);
  const [hasSeededBackground, setHasSeededBackground] = useState(false);
  const [isSavingSpaces, setIsSavingSpaces] = useState(false);
  const [availableScenarios, setAvailableScenarios] = useState([]);

  useEffect(() => {
    let cancelled = false;
    scenarioService
      .getScenarios({ limit: 200 })
      .then((response) => {
        if (cancelled) return;
        const items = Array.isArray(response)
          ? response
          : (response?.items ?? response?.scenarios ?? response?.data ?? []);
        setAvailableScenarios(items);
      })
      .catch(() => {
        // Non-fatal: editor still works without the picker.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const svgRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const isMouseDownRef = useRef(false);
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;
  const GRID_SIZE = 20;
  const [elements, setElements] = useState([]);
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const [selectedElements, setSelectedElements] = useState([]);
  const [tool, setTool] = useState("drag");
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [nextElementId, setNextElementId] = useState(1);

  useEffect(() => {
    setHasSeededSpaces(false);
    setHasSeededBackground(false);
    setElements([]);
    setSelectedElements([]);
  }, [scenarioId]);

  useEffect(() => {
    if (!scenarioId || spacesLoading || hasSeededSpaces) return;
    if (!Array.isArray(spaces) || spaces.length === 0) return;
    const seeded = spaces.map((space) => spaceToElement(space, LAYERS.MAIN));
    setElements(seeded);
    const maxNumericId = seeded.reduce((acc, el) => {
      const n = Number(el.id);
      return Number.isFinite(n) && n > acc ? n : acc;
    }, 0);
    setNextElementId(maxNumericId + 1);
    setHasSeededSpaces(true);
  }, [scenarioId, spaces, spacesLoading, hasSeededSpaces]);

  const updateSpaceMetadata = useCallback((elementId, patch) => {
    setElements((prev) =>
      prev.map((el) => (el.id === elementId ? { ...el, ...patch } : el)),
    );
  }, []);

  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [clipboard, setClipboard] = useState([]);
  const [rightPanel, setRightPanel] = useState("layers");
  const [showPasteSVGDialog, setShowPasteSVGDialog] = useState(false);
  const [pasteSVGCode, setPasteSVGCode] = useState("");
  // Fit imported elements into the canvas viewBox (overlay the background)
  // rather than keeping the source SVG's own — often off-screen — coordinates.
  const [fitImportToCanvas, setFitImportToCanvas] = useState(true);
  const [_showFloatingPanel, setShowFloatingPanel] = useState(true);
  const [panelPosition, setPanelPosition] = useState({
    x: window.innerWidth - 320,
    y: 80,
  });

  const [isRightPanelFloating, setIsRightPanelFloating] = useState(false);
  const [isRightPanelMinimized, setIsRightPanelMinimized] = useState(false);
  const [rightPanelPosition, setRightPanelPosition] = useState({
    x: window.innerWidth - 340,
    y: 120,
  });
  const [showRightPanel, setShowRightPanel] = useState(true);

  const [currentLayer, setCurrentLayer] = useState(LAYERS.MAIN);
  const [layerVisibility, setLayerVisibility] = useState({
    [LAYERS.BACKGROUND]: true,
    [LAYERS.MAIN]: true,
    [LAYERS.OVERLAY]: true,
  });

  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImageURL, setBackgroundImageURL] = useState("");

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [, setEditingPoints] = useState(false);
  const [, setSelectedPointIndex] = useState(-1);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isCreatingPolygon, setIsCreatingPolygon] = useState(false);
  const [isSelectingBox, setIsSelectingBox] = useState(false);
  const [selectionBoxStart, setSelectionBoxStart] = useState(null);
  const [selectionBoxEnd, setSelectionBoxEnd] = useState(null);
  const [showParkObjectsDropdown, setShowParkObjectsDropdown] = useState(false);

  const [referencePoints, setReferencePoints] = useState([]);
  const [scenarioMetadata, setScenarioMetadata] = useState({
    name: "Untitled Scenario",
    description: "",
    viewBox: { ...DEFAULT_VIEWBOX },
    backgroundZoom: 1,
    coordinateSystem: "svg",
    scale: null,
    osmCenter: null,
    osmZoom: 15,
  });
  const [showScenarioSettings, setShowScenarioSettings] = useState(false);

  // Title the editor with the selected scenario's real name (e.g. "Markt
  // mittel") instead of the "Untitled Scenario" placeholder. Runs when the
  // scenario or the loaded list changes; later manual edits to the name aren't
  // clobbered because the deps don't change.
  useEffect(() => {
    if (!scenarioId || !availableScenarios.length) return;
    const selected = availableScenarios.find(
      (s) => Number(s.id ?? s.scenario_id) === scenarioId,
    );
    const name = selected?.name?.trim();
    if (name) {
      setScenarioMetadata((prev) =>
        prev.name === name ? prev : { ...prev, name },
      );
    }
  }, [scenarioId, availableScenarios]);

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupVisibility, setGroupVisibility] = useState({});
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupNameForModal, setNewGroupNameForModal] = useState("");

  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [spaceModalElementId, setSpaceModalElementId] = useState(null);
  const [spaceModalMode, setSpaceModalMode] = useState("edit");

  const openSpaceModalForEdit = useCallback((elementId) => {
    setSpaceModalElementId(elementId);
    setSpaceModalMode("edit");
    setShowSpaceModal(true);
  }, []);

  const openSpaceModalForAdd = useCallback(() => {
    const newId = generateId();
    const placeholder = {
      id: newId,
      type: "rect",
      layer: "main",
      x: 100,
      y: 100,
      width: 120,
      height: 80,
      fill: "#e3f2fd",
      stroke: "#1976d2",
      strokeWidth: 2,
      locked: false,
      visible: true,
      label: "",
      rotation: 0,
      space_code: "",
      space_number: "",
      name: "",
      description: "",
      space_type: "event_area",
      capacity: null,
      capacity_unit: "persons",
      maintenance_status: "operational",
      gps_lat: null,
      gps_lng: null,
      orientation: null,
      area_sqm: null,
      length_m: null,
      width_m: null,
      height_m: null,
      amenities: [],
      access_control_type: null,
      access_control_device_id: "",
      is_active: true,
      is_bookable: true,
    };
    setElements((prev) => [...prev, placeholder]);
    setSelectedElements([placeholder]);
    setSpaceModalElementId(newId);
    setSpaceModalMode("add");
    setShowSpaceModal(true);
  }, []);

  const closeSpaceModal = useCallback(() => {
    if (spaceModalMode === "add" && spaceModalElementId) {
      setElements((prev) => {
        const el = prev.find((e) => e.id === spaceModalElementId);
        if (el && !(el.space_code || "").trim() && !(el.name || "").trim()) {
          return prev.filter((e) => e.id !== spaceModalElementId);
        }
        return prev;
      });
    }
    setShowSpaceModal(false);
    setSpaceModalElementId(null);
  }, [spaceModalMode, spaceModalElementId]);

  const [activePolygon, setActivePolygon] = useState(null);
  const [draggingVertexIndex, setDraggingVertexIndex] = useState(-1);
  const [draggingPolygon, setDraggingPolygon] = useState(false);
  const [hoverVertexIndex, setHoverVertexIndex] = useState(-1);
  const [hoverEdgeIndex, setHoverEdgeIndex] = useState(-1);
  const [svgZoom, setSvgZoom] = useState(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const selectedElement =
    selectedElements.length === 1 ? selectedElements[0] : null;

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (svgZoom === null && containerSize.width > 0) {
      setSvgZoom(0.95);
    }
  }, [svgZoom, containerSize]);

  const effectiveZoom = svgZoom ?? 1;

  const hasCenteredRef = useRef(false);
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container || svgZoom === null || hasCenteredRef.current) return;
    hasCenteredRef.current = true;
    const frame = requestAnimationFrame(() => {
      if (container.scrollWidth > container.clientWidth)
        container.scrollLeft =
          (container.scrollWidth - container.clientWidth) / 2;
      if (container.scrollHeight > container.clientHeight)
        container.scrollTop =
          (container.scrollHeight - container.clientHeight) / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [svgZoom]);

  const initializeGroups = useCallback(() => {
    if (!svgRef.current) return;
    initializeMissingGroups(svgRef.current);

    const allGroups = Array.from(svgRef.current.querySelectorAll("g[id]"));
    const groupData = allGroups.map((g) => ({
      id: g.id,
      name: g.id,
      element: g,
      parent: g.parentElement?.id || null,
    }));
    setGroups(groupData);

    const initialVisibility = {};
    groupData.forEach((g) => {
      initialVisibility[g.id] = false;
    });
    if (groupData.length > 0 && !selectedGroupId) {
      setSelectedGroupId(groupData[0].id);
      initialVisibility[groupData[0].id] = true;
    } else if (selectedGroupId) {
      initialVisibility[selectedGroupId] = true;
    }
    setGroupVisibility(initialVisibility);
  }, [selectedGroupId]);

  const addGroup = useCallback(
    (name) => {
      if (!svgRef.current || !name.trim()) return;
      const ns = "http://www.w3.org/2000/svg";
      const layer1 = svgRef.current.querySelector("g#layer1") || svgRef.current;
      const newGroup = document.createElementNS(ns, "g");
      const groupId = name.trim().toLowerCase().replace(/\s+/g, "_");
      newGroup.setAttribute("id", groupId);
      layer1.appendChild(newGroup);
      initializeGroups();
      setNewGroupName("");
    },
    [initializeGroups],
  );

  const updateGroup = useCallback(
    (groupId, newName) => {
      if (!svgRef.current || !newName.trim()) return;
      const group = svgRef.current.querySelector(`g#${CSS.escape(groupId)}`);
      if (group) {
        const newId = newName.trim().toLowerCase().replace(/\s+/g, "_");
        group.setAttribute("id", newId);
        initializeGroups();
        if (selectedGroupId === groupId) {
          setSelectedGroupId(newId);
        }
        setEditingGroupId(null);
      }
    },
    [initializeGroups, selectedGroupId],
  );

  const deleteGroup = useCallback(
    (groupId) => {
      if (!svgRef.current) return;
      const group = svgRef.current.querySelector(`g#${CSS.escape(groupId)}`);
      if (group && group.id !== "layer1") {
        const groupElements = elements.filter((el) => el.groupId === groupId);
        if (groupElements.length > 0) {
          setElements((prev) => prev.filter((el) => el.groupId !== groupId));
        }
        group.remove();
        initializeGroups();
        if (selectedGroupId === groupId) {
          const remaining = groups.filter((g) => g.id !== groupId);
          setSelectedGroupId(remaining.length > 0 ? remaining[0].id : null);
        }
      }
    },
    [svgRef, elements, groups, selectedGroupId, initializeGroups],
  );

  const selectGroup = useCallback(
    (groupId) => {
      setSelectedGroupId(groupId);
      const newVisibility = {};
      groups.forEach((g) => {
        newVisibility[g.id] = g.id === groupId;
      });
      setGroupVisibility(newVisibility);
      setActivePolygon(null);
    },
    [groups],
  );

  const pointToSegmentDistance = usePointToSegmentDistance();
  const getNearestVertex = useGetNearestVertex();
  const getNearestEdge = useGetNearestEdge(pointToSegmentDistance);
  const pixelToLngLat = usePixelToLngLat(CANVAS_WIDTH, CANVAS_HEIGHT);
  const lngLatToPixel = useLngLatToPixel(CANVAS_WIDTH, CANVAS_HEIGHT);

  const updateElement = useCallback((id, updates) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el)),
    );
    setSelectedElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el)),
    );
  }, []);

  const addVertexToPolygon = useCallback(
    (polygonId, edgeIndex, point) => {
      const element = elements.find((el) => el.id === polygonId);
      if (!element || !element.points || edgeIndex === null) return;
      const newPoints = [...element.points];
      newPoints.splice(edgeIndex + 1, 0, point);
      updateElement(polygonId, { points: newPoints });
      setActivePolygon({ ...element, points: newPoints });
    },
    [elements, updateElement],
  );

  const deleteVertexFromPolygon = useCallback(
    (polygonId, vertexIndex) => {
      const element = elements.find((el) => el.id === polygonId);
      if (!element || !element.points || element.points.length <= 3) return;
      const newPoints = element.points.filter(
        (_, index) => index !== vertexIndex,
      );
      updateElement(polygonId, { points: newPoints });
      setActivePolygon({ ...element, points: newPoints });
    },
    [elements, updateElement],
  );

  const canUndo = useMemo(() => historyIndex > 0, [historyIndex]);
  const canRedo = useMemo(
    () => historyIndex < history.length - 1,
    [historyIndex, history.length],
  );
  const hasSelection = useMemo(
    () => selectedElements.length > 0,
    [selectedElements.length],
  );
  const canAlign = useMemo(
    () => selectedElements.length >= 2,
    [selectedElements.length],
  );

  const {
    calculateDistance: _calculateDistance,
    calculateScale: _calculateScale,
    updateScenarioScale,
    toggleReferencePoint,
    updatePinGPS: updatePinGPSHelper,
  } = useGPSHelpers({
    referencePoints,
    setReferencePoints,
    elements,
    scenarioMetadata,
    setScenarioMetadata,
    setElements,
    setSelectedElements,
  });

  const updatePinGPS = useCallback(
    (pinId, latitude, longitude) => {
      updatePinGPSHelper(pinId, latitude, longitude, updateScenarioScale);
    },
    [updatePinGPSHelper, updateScenarioScale],
  );

  const saveToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      elements: JSON.parse(JSON.stringify(elements)),
      timestamp: Date.now(),
    });
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex((prev) => prev + 1);
    }
    setHistory(newHistory);
  }, [elements, history, historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setElements(history[newIndex].elements);
      setHistoryIndex(newIndex);
      setSelectedElements([]);
    }
  }, [history, historyIndex]);
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setElements(history[newIndex].elements);
      setHistoryIndex(newIndex);
      setSelectedElements([]);
    }
  }, [history, historyIndex]);

  const snapToGridCoords = useSnapToGridCoords(snapToGrid);
  const getElementBounds = useGetElementBounds();
  const isPointInBounds = useIsPointInBounds();
  const getSVGCoordinatesHook = useGetSVGCoordinates(snapToGridCoords);

  const getSVGCoordinates = useCallback(
    (e) => {
      return getSVGCoordinatesHook(e, svgRef);
    },
    [getSVGCoordinatesHook],
  );

  const toggleLayerVisibility = useCallback((layerId) => {
    setLayerVisibility((prev) => ({
      ...prev,
      [layerId]: !prev[layerId],
    }));
  }, []);

  const getElementsForLayer = useCallback(
    (layerId) => {
      return elements.filter((el) => el.layer === layerId);
    },
    [elements],
  );

  const copyElements = useCallback(() => {
    if (selectedElements.length > 0) {
      setClipboard(selectedElements.map((el) => ({ ...el })));
    }
  }, [selectedElements]);

  const pasteElements = useCallback(() => {
    if (clipboard.length > 0) {
      saveToHistory();
      const newElements = clipboard.map((el) => ({
        ...el,
        id: generateId(),
        x: (el.x || el.cx || 0) + 20,
        y: (el.y || el.cy || 0) + 20,
        cx: el.cx ? el.cx + 20 : el.cx,
        cy: el.cy ? el.cy + 20 : el.cy,
        points: el.points
          ? el.points.map((p) => ({ x: p.x + 20, y: p.y + 20 }))
          : el.points,
      }));
      setElements((prev) => [...prev, ...newElements]);
      setSelectedElements(newElements);
    }
  }, [clipboard, saveToHistory]);

  const duplicateElements = useCallback(() => {
    if (selectedElements.length > 0) {
      saveToHistory();
      const newElements = selectedElements.map((el) => {
        const originalEl = elements.find((e) => e.id === el.id) || el;
        return {
          ...originalEl,
          id: generateId(),
          x: (originalEl.x || originalEl.cx || 0) + 20,
          y: (originalEl.y || originalEl.cy || 0) + 20,
          cx: originalEl.cx ? originalEl.cx + 20 : originalEl.cx,
          cy: originalEl.cy ? originalEl.cy + 20 : originalEl.cy,
          points: originalEl.points
            ? originalEl.points.map((p) => ({ x: p.x + 20, y: p.y + 20 }))
            : originalEl.points,
        };
      });
      setElements((prev) => [...prev, ...newElements]);
      setSelectedElements(newElements);
    }
  }, [selectedElements, elements, saveToHistory]);

  const groupElements = useCallback(() => {
    if (selectedElements.length < 2) return;
    setShowCreateGroupModal(true);
    setNewGroupNameForModal(`Group ${nextElementId}`);
  }, [selectedElements.length, nextElementId]);

  const createGroupAndAssignElements = useCallback(() => {
    if (!newGroupNameForModal.trim() || selectedElements.length < 2) return;
    if (!svgRef.current) return;

    saveToHistory();

    const ns = "http://www.w3.org/2000/svg";
    const layer1 = svgRef.current.querySelector("g#layer1") || svgRef.current;
    const newGroup = document.createElementNS(ns, "g");
    const groupId = newGroupNameForModal
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    newGroup.setAttribute("id", groupId);
    layer1.appendChild(newGroup);

    // Assign selected elements to the group
    const updatedElements = elements.map((el) => {
      const isSelected = selectedElements.find((sel) => sel.id === el.id);
      if (isSelected) {
        return { ...el, groupId, isGrouped: true };
      }
      return el;
    });

    setElements(updatedElements);
    initializeGroups();
    setSelectedGroupId(groupId);
    setShowCreateGroupModal(false);
    setNewGroupNameForModal("");
    setNextElementId((prev) => prev + 1);
  }, [
    newGroupNameForModal,
    selectedElements,
    elements,
    saveToHistory,
    initializeGroups,
  ]);

  const alignElements = useCallback(
    (alignType) => {
      if (selectedElements.length < 2) return;
      saveToHistory();
      const bounds = selectedElements.map(getElementBounds);
      let targetValue;
      switch (alignType) {
        case "left":
          targetValue = Math.min(...bounds.map((b) => b.x));
          break;
        case "center":
          targetValue =
            bounds.reduce((sum, b) => sum + b.x + b.width / 2, 0) /
            bounds.length;
          break;
        case "right":
          targetValue = Math.max(...bounds.map((b) => b.x + b.width));
          break;
        case "top":
          targetValue = Math.min(...bounds.map((b) => b.y));
          break;
        case "middle":
          targetValue =
            bounds.reduce((sum, b) => sum + b.y + b.height / 2, 0) /
            bounds.length;
          break;
        case "bottom":
          targetValue = Math.max(...bounds.map((b) => b.y + b.height));
          break;
        default:
          return;
      }
      const updatedElements = elements.map((el) => {
        const selectedEl = selectedElements.find((sel) => sel.id === el.id);
        if (!selectedEl) return el;
        const b = getElementBounds(selectedEl);
        const updates = {};
        switch (alignType) {
          case "left":
            if (el.type === "rect") updates.x = targetValue;
            else if (["circle", "tree"].includes(el.type))
              updates.cx = targetValue + b.width / 2;
            else if (el.type === "text") updates.x = targetValue;
            break;
          case "center":
            if (el.type === "rect") updates.x = targetValue - b.width / 2;
            else if (["circle", "tree"].includes(el.type))
              updates.cx = targetValue;
            else if (el.type === "text") updates.x = targetValue;
            break;
          case "right":
            if (el.type === "rect") updates.x = targetValue - b.width;
            else if (["circle", "tree"].includes(el.type))
              updates.cx = targetValue - b.width / 2;
            else if (el.type === "text") updates.x = targetValue;
            break;
          case "top":
            if (el.type === "rect") updates.y = targetValue;
            else if (["circle", "tree"].includes(el.type))
              updates.cy = targetValue + b.height / 2;
            else if (el.type === "text") updates.y = targetValue + b.height;
            break;
          case "middle":
            if (el.type === "rect") updates.y = targetValue - b.height / 2;
            else if (["circle", "tree"].includes(el.type))
              updates.cy = targetValue;
            else if (el.type === "text") updates.y = targetValue;
            break;
          case "bottom":
            if (el.type === "rect") updates.y = targetValue - b.height;
            else if (["circle", "tree"].includes(el.type))
              updates.cy = targetValue - b.height / 2;
            else if (el.type === "text") updates.y = targetValue;
            break;
          default:
            break;
        }
        return { ...el, ...updates };
      });
      setElements(updatedElements);
      setSelectedElements(
        updatedElements.filter((el) =>
          selectedElements.find((sel) => sel.id === el.id),
        ),
      );
    },
    [selectedElements, elements, saveToHistory, getElementBounds],
  );

  const rotateElement = useCallback(
    (elementId, angle) => {
      saveToHistory();
      updateElement(elementId, {
        rotation:
          ((elements.find((el) => el.id === elementId)?.rotation || 0) +
            angle) %
          360,
      });
    },
    [elements, saveToHistory, updateElement],
  );

  const changeZOrder = useCallback(
    (direction) => {
      if (selectedElements.length === 0) return;
      saveToHistory();
      const reorderedElements = [...elements];
      selectedElements.forEach((selectedEl) => {
        const currentIndex = reorderedElements.findIndex(
          (el) => el.id === selectedEl.id,
        );
        if (currentIndex === -1) return;
        const element = reorderedElements.splice(currentIndex, 1)[0];
        if (direction === "front") {
          reorderedElements.push(element);
        } else {
          reorderedElements.unshift(element);
        }
      });
      setElements(reorderedElements);
    },
    [selectedElements, elements, saveToHistory],
  );

  const _getDefaultLayer = useCallback((type) => {
    switch (type) {
      case "rectangle":
      case "circle":
        return LAYERS.MAIN;
      case "polygon":
        return LAYERS.BACKGROUND;
      case "text":
      case "pin":
        return LAYERS.OVERLAY;
      default: {
        const parkObject = Object.values(PARK_OBJECTS).find((obj) =>
          obj.label.toLowerCase().includes(type),
        );
        return parkObject?.defaultLayer || LAYERS.MAIN;
      }
    }
  }, []);

  const addElement = useCallback(
    (type, x, y) => {
      if (!scenarioId) {
        toast.error(
          "Bitte zuerst ein Szenario auswählen, um Flächen anzulegen.",
        );
        return;
      }
      if (!currentLayer) {
        // eslint-disable-next-line no-alert
        alert(
          "Please select a target layer before placing objects. Use the Layers panel to choose a layer.",
        );
        return;
      }
      const id = generateId();
      let newElement;
      const targetLayer = currentLayer;
      switch (type) {
        case "rectangle":
          newElement = {
            id,
            type: "rect",
            layer: targetLayer,
            x: x - 50,
            y: y - 25,
            width: 100,
            height: 50,
            fill: "#e3f2fd",
            stroke: "#1976d2",
            strokeWidth: 2,
            rotation: 0,
            locked: false,
            visible: true,
            label: `Space ${nextElementId}`,
          };
          break;
        case "circle":
          newElement = {
            id,
            type: "circle",
            layer: targetLayer,
            cx: x,
            cy: y,
            r: 30,
            fill: "#f3e5f5",
            stroke: "#7b1fa2",
            strokeWidth: 2,
            rotation: 0,
            locked: false,
            visible: true,
            label: `Area ${nextElementId}`,
          };
          break;
        case "text":
          newElement = {
            id,
            type: "text",
            layer: targetLayer,
            x,
            y,
            text: `Label ${nextElementId}`,
            fontSize: 14,
            fill: "#333",
            fontFamily: "Arial, sans-serif",
            rotation: 0,
            locked: false,
            visible: true,
          };
          break;
        case "pin":
          newElement = {
            id,
            type: "pin",
            layer: targetLayer,
            x: x - 10,
            y: y - 20,
            fill: "#f44336",
            stroke: "#d32f2f",
            strokeWidth: 2,
            rotation: 0,
            locked: false,
            visible: true,
            label: `Pin ${nextElementId}`,
            gps: null,
            isReferencePoint: false,
          };
          break;
        case "polygon":
          if (!selectedGroupId) {
            // eslint-disable-next-line no-alert
            alert(
              "Please select a group first. Use the group selector in the toolbar.",
            );
            return;
          }
          setIsCreatingPolygon(true);
          setPolygonPoints([{ x, y }]);
          return;
        case "tree":
        case "stage":
        case "seating":
        case "parking":
        case "toilet":
        case "trash":
        case "fountain_drink":
        case "fountain_water":
        case "lighting":
        case "fence":
        case "info_board":
        case "entrance":
          if (PARK_OBJECTS[type]) {
            const parkObject = PARK_OBJECTS[type];
            const geometry = parkObject.geometry || {};
            const isRectangular = [
              "stage",
              "seating",
              "parking",
              "toilet",
              "fence",
              "entrance",
            ].includes(type);
            newElement = {
              id,
              type: type,
              subType: "parkObject",
              layer: targetLayer,
              fill: parkObject.fill,
              stroke: parkObject.stroke,
              strokeWidth: 2,
              rotation: 0,
              locked: false,
              visible: true,
              label: `${parkObject.label} ${nextElementId}`,
            };
            if (isRectangular) {
              newElement.x = x - 30;
              newElement.y = y - 15;
              newElement.width = type === "parking" ? 80 : 60;
              newElement.height = type === "parking" ? 40 : 30;
            } else {
              const baseRadius =
                geometry.radius ?? (type === "fountain_water" ? 25 : 15);
              newElement.cx = x;
              newElement.cy = y;
              newElement.r = baseRadius;
              newElement.baseGeometry = {
                ...geometry,
                radius: geometry.radius ?? baseRadius,
              };
              newElement.scale = 1;
            }
          } else {
            // eslint-disable-next-line no-console
            console.error(`Unknown park object type: ${type}`);
            return;
          }
          break;
        default:
          // eslint-disable-next-line no-console
          console.error(`Unknown element type: ${type}`);
          return;
      }
      saveToHistory();
      setElements((prev) => [...prev, newElement]);
      setSelectedElements([newElement]);
      setNextElementId((prev) => prev + 1);
      setTool("select");
      // Show the new element's Properties immediately so its info can be filled.
      setRightPanel("properties");
      setShowRightPanel(true);
    },
    [nextElementId, currentLayer, saveToHistory, scenarioId, selectedGroupId],
  );

  const completePolygon = useCallback(() => {
    if (!currentLayer) {
      // eslint-disable-next-line no-alert
      alert(
        "Please select a target layer before creating objects. Use the Layers panel to choose a layer.",
      );
      return;
    }
    if (polygonPoints.length >= 3) {
      const id = generateId();
      let fillColor = "#fff3e0";
      let strokeColor = "#f57c00";
      if (currentLayer === LAYERS.BACKGROUND) {
        fillColor = "#e8f5e8";
        strokeColor = "#4caf50";
      } else if (currentLayer === LAYERS.OVERLAY) {
        fillColor = "#f3e5f5";
        strokeColor = "#9c27b0";
      }
      const newElement = {
        id,
        type: "polygon",
        layer: currentLayer,
        groupId: selectedGroupId, // Assign to selected group
        points: [...polygonPoints],
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: 2,
        rotation: 0,
        locked: false,
        visible: true,
        label: `Polygon ${nextElementId}`,
        polygonId: null,
        coordinateSystem: scenarioMetadata.coordinateSystem,
      };
      saveToHistory();
      setElements((prev) => [...prev, newElement]);
      setSelectedElements([newElement]);
      setNextElementId((prev) => prev + 1);
      setRightPanel("properties");
      setShowRightPanel(true);
    }
    setIsCreatingPolygon(false);
    setPolygonPoints([]);
    setTool("select");
  }, [polygonPoints, nextElementId, currentLayer, saveToHistory]);

  // Background image handling - using hook
  const { importBackgroundImage, importBackgroundFromURL } = useBackgroundImage(
    {
      scenarioMetadata,
      setBackgroundImage,
      setLayerVisibility,
    },
  );

  useEffect(() => {
    if (!scenarioId || hasSeededBackground) return;
    const svgContent = loadedScenario?.svg_content;
    if (!svgContent) return;
    try {
      const doc = new DOMParser().parseFromString(svgContent, "image/svg+xml");
      const bgZoomAttr = parseFloat(
        doc.documentElement?.getAttribute("data-background-zoom"),
      );
      if (Number.isFinite(bgZoomAttr) && bgZoomAttr > 0) {
        setScenarioMetadata((prev) => ({
          ...prev,
          backgroundZoom: bgZoomAttr,
        }));
      }
      const imageEl = doc.querySelector("image");
      const fo = doc.querySelector("foreignObject");
      if (imageEl) {
        const href =
          imageEl.getAttribute("href") ||
          imageEl.getAttribute("xlink:href") ||
          imageEl.getAttribute("data-href");
        if (href) {
          if (href.startsWith("data:")) {
            setBackgroundImage({
              type: "image",
              source: "url",
              data: href,
              width: scenarioMetadata.viewBox.width,
              height: scenarioMetadata.viewBox.height,
              originalWidth: scenarioMetadata.viewBox.width,
              originalHeight: scenarioMetadata.viewBox.height,
              url: href,
              filename: "background",
            });
            setHasSeededBackground(true);
            return;
          }
          if (href.startsWith("http")) {
            importBackgroundFromURL(href);
            setHasSeededBackground(true);
            return;
          }
        }
      }
      if (fo) {
        const inner = fo.innerHTML?.trim();
        if (inner) {
          setBackgroundImage({
            type: "svg",
            source: "url",
            data: inner,
            width: scenarioMetadata.viewBox.width,
            height: scenarioMetadata.viewBox.height,
            filename: "background.svg",
          });
          setHasSeededBackground(true);
          return;
        }
      }
      setHasSeededBackground(true);
    } catch (err) {
      console.error("Failed to restore scenario background:", err);
      setHasSeededBackground(true);
    }
  }, [
    scenarioId,
    loadedScenario,
    hasSeededBackground,
    importBackgroundFromURL,
    scenarioMetadata.viewBox.width,
    scenarioMetadata.viewBox.height,
  ]);

  const getResizeHandle = useCallback((coords, element) => {
    const circularTypes = [
      "circle",
      "tree",
      "trash",
      "fountain_drink",
      "lighting",
      "info_board",
      "fountain_water",
    ];
    const rectangularTypes = [
      "rect",
      "stage",
      "seating",
      "parking",
      "toilet",
      "fence",
      "entrance",
    ];
    if (
      !circularTypes.includes(element.type) &&
      !rectangularTypes.includes(element.type)
    )
      return null;
    const threshold = 8;
    if (rectangularTypes.includes(element.type)) {
      const { x, y, width, height } = element;
      const handles = {
        nw: { x, y },
        ne: { x: x + width, y },
        sw: { x, y: y + height },
        se: { x: x + width, y: y + height },
      };
      for (const [handle, pos] of Object.entries(handles)) {
        if (distance(coords, pos) <= threshold) {
          return handle;
        }
      }
    } else if (circularTypes.includes(element.type)) {
      const handlePos = { x: element.cx + element.r, y: element.cy };
      if (distance(coords, handlePos) <= threshold) {
        return "resize";
      }
    }
    return null;
  }, []);

  const handleElementClick = useCallback(
    (e, element) => {
      e.stopPropagation();
      if (tool === "select" || tool === "drag" || tool === "edit") {
        if (e.ctrlKey || e.metaKey) {
          setSelectedElements((prev) =>
            prev.find((el) => el.id === element.id)
              ? prev.filter((el) => el.id !== element.id)
              : [...prev, element],
          );
        } else {
          setSelectedElements([element]);
          // Open Properties for ANY selected element — newly-drawn spaces have
          // no spaceId yet, but must still be editable (label, layer, etc.).
          setRightPanel("properties");
          setShowRightPanel(true);
        }
        setEditingPoints(tool === "edit");
      }
    },
    [tool],
  );

  const deleteSelected = useCallback(() => {
    if (selectedElements.length > 0) {
      saveToHistory();
      setElements((prev) =>
        prev.filter((el) => !selectedElements.find((sel) => sel.id === el.id)),
      );
      setSelectedElements([]);
    }
  }, [selectedElements, saveToHistory]);

  const toggleElementLock = useCallback(
    (elementId) => {
      const element = elements.find((el) => el.id === elementId);
      if (element) {
        updateElement(elementId, { locked: !element.locked });
      }
    },
    [elements, updateElement],
  );

  const toggleElementVisibility = useCallback(
    (elementId) => {
      const element = elements.find((el) => el.id === elementId);
      if (element) {
        updateElement(elementId, { visible: !element.visible });
      }
    },
    [elements, updateElement],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (!svgRef.current) return;
      const coords = getSVGCoordinates(e);
      isMouseDownRef.current = true;

      if (tool === "edit" && activePolygon) {
        const element = elements.find((el) => el.id === activePolygon.id);
        if (element && element.points) {
          const vertexIdx = getNearestVertex(element, coords, 8);
          if (vertexIdx !== null && vertexIdx !== undefined) {
            setDraggingVertexIndex(vertexIdx);
            setDragStart(coords);
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          const polygonBounds = getElementBounds(element);
          if (isPointInBounds(coords, polygonBounds)) {
            setDraggingPolygon(true);
            setDragStart(coords);
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
      }
      if (tool === "polygon" && isCreatingPolygon) {
        setPolygonPoints((prev) => [...prev, coords]);
        return;
      }
      if (
        tool !== "select" &&
        tool !== "drag" &&
        tool !== "edit" &&
        !isCreatingPolygon
      ) {
        if (Object.keys(PARK_OBJECTS).includes(tool)) {
          addElement(tool, coords.x, coords.y);
        } else {
          addElement(tool, coords.x, coords.y);
        }
        return;
      }
      if (selectedElement && tool === "select") {
        const handle = getResizeHandle(coords, selectedElement);
        if (handle) {
          setIsResizing(true);
          setResizeHandle(handle);
          setDragStart(coords);
          return;
        }
      }
      // Pick the SMALLEST element under the cursor (not just the topmost), so
      // small shapes stacked inside a large container — e.g. market stands
      // drawn over a site-outline polygon — can be grabbed and dragged instead
      // of always hitting the big overlapping area underneath them.
      let clickedElement = null;
      let smallestArea = Infinity;
      for (let i = elements.length - 1; i >= 0; i--) {
        const element = elements[i];
        if (!element.visible || element.locked) continue;
        const bounds = getElementBounds(element);
        if (isPointInBounds(coords, bounds)) {
          const area = (bounds.width || 0) * (bounds.height || 0);
          if (area < smallestArea) {
            smallestArea = area;
            clickedElement = element;
          }
        }
      }
      if (clickedElement) {
        if (!clickedElement.locked) {
          handleElementClick(e, clickedElement);
          if (
            (tool === "drag" || tool === "select") &&
            !(e.ctrlKey || e.metaKey) &&
            selectedElements.find((el) => el.id === clickedElement.id)
          ) {
            setIsDragging(true);
            setDragStart(coords);
          }
        }
      } else {
        if (tool === "select" && !(e.ctrlKey || e.metaKey)) {
          setSelectedElements([]);
          setIsSelectingBox(true);
          setSelectionBoxStart(coords);
          setSelectionBoxEnd(coords);
        }
        if (tool === "drag") {
          setIsPanning(true);
          setPanStart({ x: e.clientX, y: e.clientY });
          e.preventDefault();
        }
      }
    },
    [
      tool,
      selectedElement,
      selectedElements,
      isCreatingPolygon,
      getSVGCoordinates,
      addElement,
      getResizeHandle,
      getElementBounds,
      isPointInBounds,
      handleElementClick,
      elements,
      activePolygon,
      getNearestVertex,
      getNearestEdge,
      draggingVertexIndex,
      draggingPolygon,
      updateElement,
      setIsSelectingBox,
      setSelectionBoxStart,
      setSelectionBoxEnd,
    ],
  );

  const handleMoveLogic = useCallback(
    (e) => {
      if (isPanning) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;
        const container = canvasContainerRef.current;
        if (container) {
          container.scrollLeft -= dx;
          container.scrollTop -= dy;
        }
        setPanStart({ x: e.clientX, y: e.clientY });
        e.preventDefault();
        return;
      }

      const coords = getSVGCoordinates(e);

      if (draggingVertexIndex >= 0 && activePolygon) {
        const element = elements.find((el) => el.id === activePolygon.id);
        if (element && element.points) {
          const coordSystem =
            element.coordinateSystem ||
            scenarioMetadata.coordinateSystem ||
            "svg";
          const newPoints = [...element.points];

          if (coordSystem === "osm") {
            const center = scenarioMetadata.osmCenter;
            const zoom = scenarioMetadata.osmZoom || 15;
            if (center) {
              const lngLat = pixelToLngLat(coords.x, coords.y, center, zoom);
              newPoints[draggingVertexIndex] = {
                lng: lngLat.lng,
                lat: lngLat.lat,
              };
            } else {
              newPoints[draggingVertexIndex] = { lng: coords.x, lat: coords.y };
            }
          } else {
            newPoints[draggingVertexIndex] = { x: coords.x, y: coords.y };
          }

          updateElement(element.id, { points: newPoints });
          setActivePolygon({ ...element, points: newPoints });
        }
        return;
      }

      if (draggingPolygon && activePolygon && dragStart) {
        const element = elements.find((el) => el.id === activePolygon.id);
        if (element && element.points) {
          const coordSystem =
            element.coordinateSystem ||
            scenarioMetadata.coordinateSystem ||
            "svg";
          const dx = coords.x - dragStart.x;
          const dy = coords.y - dragStart.y;

          if (coordSystem === "osm") {
            const center = scenarioMetadata.osmCenter;
            const zoom = scenarioMetadata.osmZoom || 15;
            if (center) {
              const deltaLngLat = pixelToLngLat(dx, dy, center, zoom);
              const newPoints = element.points.map((p) => ({
                lng: (p.lng || p.x) + deltaLngLat.lng,
                lat: (p.lat || p.y) + deltaLngLat.lat,
              }));
              updateElement(element.id, { points: newPoints });
              setActivePolygon({ ...element, points: newPoints });
            }
          } else {
            const newPoints = element.points.map((p) => ({
              x: (p.x || p.lng) + dx,
              y: (p.y || p.lat) + dy,
            }));
            updateElement(element.id, { points: newPoints });
            setActivePolygon({ ...element, points: newPoints });
          }
          setDragStart(coords);
        }
        return;
      }

      if (activePolygon && tool === "edit") {
        const element = elements.find((el) => el.id === activePolygon.id);
        if (element && element.points) {
          const edgeIdx = getNearestEdge(element, coords, 12);
          setHoverEdgeIndex(edgeIdx);
          if (edgeIdx === null) {
            const vertexIdx = getNearestVertex(element, coords, 12);
            setHoverVertexIndex(vertexIdx);
          } else {
            setHoverVertexIndex(null);
          }
        }
      }

      if (isSelectingBox && isMouseDownRef.current && selectionBoxStart) {
        setSelectionBoxEnd(coords);
        return;
      }

      if (!isMouseDownRef.current || (!isDragging && !isResizing)) return;
      if (!dragStart) return;
      const dx = coords.x - dragStart.x;
      const dy = coords.y - dragStart.y;
      if (isResizing && selectedElement && resizeHandle) {
        const updates = {};
        const rectangularTypes = [
          "rect",
          "stage",
          "seating",
          "parking",
          "toilet",
          "fence",
          "entrance",
        ];
        const circularTypes = [
          "circle",
          "tree",
          "trash",
          "fountain_drink",
          "lighting",
          "info_board",
          "fountain_water",
        ];
        if (rectangularTypes.includes(selectedElement.type)) {
          switch (resizeHandle) {
            case "nw":
              updates.x = selectedElement.x + dx;
              updates.y = selectedElement.y + dy;
              updates.width = Math.max(10, selectedElement.width - dx);
              updates.height = Math.max(10, selectedElement.height - dy);
              break;
            case "ne":
              updates.y = selectedElement.y + dy;
              updates.width = Math.max(10, selectedElement.width + dx);
              updates.height = Math.max(10, selectedElement.height - dy);
              break;
            case "sw":
              updates.x = selectedElement.x + dx;
              updates.width = Math.max(10, selectedElement.width - dx);
              updates.height = Math.max(10, selectedElement.height + dy);
              break;
            case "se":
              updates.width = Math.max(10, selectedElement.width + dx);
              updates.height = Math.max(10, selectedElement.height + dy);
              break;
            default:
              break;
          }
        } else if (circularTypes.includes(selectedElement.type)) {
          const baseGeom = selectedElement.baseGeometry;
          const centerX = selectedElement.cx ?? selectedElement.x ?? 0;
          const centerY = selectedElement.cy ?? selectedElement.y ?? 0;
          const dist = Math.sqrt(
            (coords.x - centerX) * (coords.x - centerX) +
              (coords.y - centerY) * (coords.y - centerY),
          );
          const newRadius = Math.max(5, dist);
          updates.r = newRadius;
          if (baseGeom?.radius) {
            updates.scale = newRadius / baseGeom.radius;
          }
        }
        updateElement(selectedElement.id, updates);
        setDragStart(coords);
      } else if (isDragging && selectedElements.length > 0) {
        selectedElements.forEach((el) => {
          if (el.locked) return;
          const updates = {};
          const rectangularTypes = [
            "rect",
            "stage",
            "seating",
            "parking",
            "toilet",
            "fence",
            "entrance",
          ];
          const circularTypes = [
            "circle",
            "tree",
            "trash",
            "fountain_drink",
            "lighting",
            "info_board",
            "fountain_water",
          ];
          if (rectangularTypes.includes(el.type)) {
            updates.x = el.x + dx;
            updates.y = el.y + dy;
          } else if (circularTypes.includes(el.type)) {
            updates.cx = el.cx + dx;
            updates.cy = el.cy + dy;
          } else if (el.type === "text") {
            updates.x = el.x + dx;
            updates.y = el.y + dy;
          } else if (el.type === "pin") {
            updates.x = el.x + dx;
            updates.y = el.y + dy;
          } else if (el.type === "polygon" && el.points) {
            const coordSystem =
              el.coordinateSystem || scenarioMetadata.coordinateSystem || "svg";
            if (coordSystem === "osm") {
              const center = scenarioMetadata.osmCenter;
              const zoom = scenarioMetadata.osmZoom || 15;
              if (center) {
                const deltaLngLat = pixelToLngLat(dx, dy, center, zoom);
                updates.points = el.points.map((point) => ({
                  lng: (point.lng || point.x) + deltaLngLat.lng,
                  lat: (point.lat || point.y) + deltaLngLat.lat,
                }));
              }
            } else {
              updates.points = el.points.map((point) => ({
                x: (point.x || point.lng) + dx,
                y: (point.y || point.lat) + dy,
              }));
            }
          }
          updateElement(el.id, updates);
        });
        setDragStart(coords);
      }
    },
    [
      isPanning,
      panStart,
      isDragging,
      isResizing,
      selectedElement,
      selectedElements,
      dragStart,
      resizeHandle,
      getSVGCoordinates,
      updateElement,
      draggingVertexIndex,
      activePolygon,
      draggingPolygon,
      elements,
      scenarioMetadata,
      tool,
      getNearestEdge,
      getNearestVertex,
      isSelectingBox,
      selectionBoxStart,
      setSelectionBoxEnd,
    ],
  );

  const moveRafRef = useRef(0);
  const pendingMoveEventRef = useRef(null);

  const flushPendingMove = useCallback(() => {
    if (moveRafRef.current) {
      cancelAnimationFrame(moveRafRef.current);
      moveRafRef.current = 0;
    }
    const ev = pendingMoveEventRef.current;
    pendingMoveEventRef.current = null;
    if (ev) handleMoveLogic(ev);
  }, [handleMoveLogic]);

  const handleMouseMove = useCallback(
    (e) => {
      if (isPanning) {
        handleMoveLogic(e);
        return;
      }
      pendingMoveEventRef.current = e;
      if (moveRafRef.current) return;
      moveRafRef.current = requestAnimationFrame(() => {
        moveRafRef.current = 0;
        const ev = pendingMoveEventRef.current;
        pendingMoveEventRef.current = null;
        if (ev) handleMoveLogic(ev);
      });
    },
    [handleMoveLogic, isPanning],
  );

  useEffect(() => {
    return () => {
      if (moveRafRef.current) cancelAnimationFrame(moveRafRef.current);
    };
  }, []);

  const handleMouseUp = useCallback(() => {
    flushPendingMove();
    if (
      isDragging ||
      isResizing ||
      draggingVertexIndex >= 0 ||
      draggingPolygon
    ) {
      saveToHistory();
    }

    if (isSelectingBox && selectionBoxStart && selectionBoxEnd) {
      const box = {
        x: Math.min(selectionBoxStart.x, selectionBoxEnd.x),
        y: Math.min(selectionBoxStart.y, selectionBoxEnd.y),
        width: Math.abs(selectionBoxEnd.x - selectionBoxStart.x),
        height: Math.abs(selectionBoxEnd.y - selectionBoxStart.y),
      };

      if (box.width > 5 && box.height > 5) {
        const elementsInBox = elements.filter((el) => {
          if (!el.visible || el.locked) return false;
          const bounds = getElementBounds(el);

          return !(
            bounds.x + bounds.width < box.x ||
            bounds.x > box.x + box.width ||
            bounds.y + bounds.height < box.y ||
            bounds.y > box.y + box.height
          );
        });

        setSelectedElements(elementsInBox);
      }

      setIsSelectingBox(false);
      setSelectionBoxStart(null);
      setSelectionBoxEnd(null);
    }

    isMouseDownRef.current = false;
    setIsDragging(false);
    setIsResizing(false);
    setIsPanning(false);
    setResizeHandle(null);
    setSelectedPointIndex(-1);
    setDraggingVertexIndex(-1);
    setDraggingPolygon(false);
    setPanStart({ x: 0, y: 0 });
  }, [
    isDragging,
    isResizing,
    draggingVertexIndex,
    draggingPolygon,
    isSelectingBox,
    selectionBoxStart,
    selectionBoxEnd,
    elements,
    getElementBounds,
    saveToHistory,
    flushPendingMove,
  ]);

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();

        if (!svgRef.current) return;
        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, effectiveZoom * zoomFactor),
        );
        const ratio = newZoom / effectiveZoom;

        setSvgZoom(newZoom);

        const prevLeft = container.scrollLeft;
        const prevTop = container.scrollTop;
        requestAnimationFrame(() => {
          container.scrollLeft = (mouseX + prevLeft) * ratio - mouseX;
          container.scrollTop = (mouseY + prevTop) * ratio - mouseY;
        });
      }
    },
    [effectiveZoom],
  );

  const handleButtonZoom = useCallback(
    (direction) => {
      const container = canvasContainerRef.current;
      if (!container) return;

      const newZoom =
        direction === "in"
          ? Math.min(effectiveZoom * 1.2, MAX_ZOOM)
          : Math.max(effectiveZoom / 1.2, MIN_ZOOM);

      const ratio = newZoom / effectiveZoom;
      const centerX = container.clientWidth / 2;
      const centerY = container.clientHeight / 2;
      const prevLeft = container.scrollLeft;
      const prevTop = container.scrollTop;

      setSvgZoom(newZoom);

      requestAnimationFrame(() => {
        container.scrollLeft = (centerX + prevLeft) * ratio - centerX;
        container.scrollTop = (centerY + prevTop) * ratio - centerY;
      });
    },
    [effectiveZoom],
  );

  const handleFitToView = useCallback(() => {
    const container = canvasContainerRef.current;
    setSvgZoom(0.95);
    if (container) {
      requestAnimationFrame(() => {
        container.scrollLeft = 0;
        container.scrollTop = 0;
      });
    }
  }, []);

  const { exportSVG, copySVGToClipboard, importSVGCode, getSerializedSVG } =
    useSVGImportExport({
      svgRef,
      scenarioMetadata,
      referencePoints,
      elements,
      currentLayer,
      nextElementId,
      setElements,
      setSelectedElements,
      setNextElementId,
      saveToHistory,
      selectedGroupId,
      setShowPasteSVGDialog,
      setPasteSVGCode,
    });

  const saveAll = useCallback(async () => {
    if (!scenarioId) {
      toast.error(
        "Kein Szenario ausgewählt. Bitte über die Flächen-Übersicht öffnen.",
      );
      return;
    }
    setIsSavingSpaces(true);
    try {
      const cleanPatch = scenarioPatch
        ? Object.fromEntries(
            Object.entries(scenarioPatch).filter(
              ([, v]) => v !== undefined && v !== null && v !== "",
            ),
          )
        : null;
      const cfgResult = await persistScenarioConfig({
        scenarioPatch: cleanPatch,
        desiredAssignments,
      });
      if (cfgResult?.errors?.length) {
        console.error("Scenario config errors:", cfgResult.errors);
      }

      const svg = getSerializedSVG();
      if (svg) {
        await scenarioService.updateScenario(scenarioId, { svg_content: svg });
      }

      // Georeference transform from the 2 GPS reference pins (if set) so each
      // space's boundary_polygon (lat/lng) is saved for map-based booking.
      const refPins = referencePoints
        .map((id) => elementsRef.current.find((el) => el.id === id))
        .filter(Boolean);
      const svgToLatLng =
        refPins.length >= 2 ? makeSvgToLatLng(refPins[0], refPins[1]) : null;

      const result = await persistSpaces(elementsRef.current, svgToLatLng);
      const { created, updated, deleted, errors } = result;
      // Write the new DB ids back onto the canvas elements so use-cases (which
      // require a spaceId) and subsequent edits target the saved record instead
      // of re-creating it — without needing a page reload.
      (created || []).forEach((c) => {
        const newId = c?.space?.id ?? c?.space?.data?.id;
        if (c?.tempId && newId) {
          updateElement(c.tempId, { spaceId: String(newId) });
        }
      });
      const allErrors = [...(cfgResult?.errors || []), ...errors];
      if (allErrors.length > 0) {
        const detail = allErrors
          .map((e) => formatErrorMessage(e.error))
          .filter(Boolean)
          .join(" • ");
        toast.error(detail || "Speichern fehlgeschlagen.", { duration: 8000 });
      } else {
        toast.success(
          `Gespeichert: Szenario + ${created.length} neu, ${updated.length} aktualisiert, ${deleted.length} gelöscht.`,
        );
      }
    } catch (err) {
      console.error("Error saving scenario/spaces:", err);
      toast.error(formatErrorMessage(err) || "Speichern fehlgeschlagen.", {
        duration: 8000,
      });
    } finally {
      setIsSavingSpaces(false);
    }
  }, [
    scenarioId,
    getSerializedSVG,
    persistSpaces,
    persistScenarioConfig,
    scenarioPatch,
    desiredAssignments,
    updateElement,
    referencePoints,
  ]);

  const [savingSingleSpace, setSavingSingleSpace] = useState(false);
  const [deletingSingleSpace, setDeletingSingleSpace] = useState(false);

  const updateSingleSpace = useCallback(
    async (elementId) => {
      if (!scenarioId) return;
      const el = elementsRef.current.find((e) => e.id === elementId);
      if (!el?.spaceId) {
        toast.error(
          "Diese Fläche existiert noch nicht im Backend. Bitte erst speichern.",
        );
        return;
      }
      setSavingSingleSpace(true);
      try {
        await spaceService.updateSpace(
          el.spaceId,
          elementToSpacePayload(el, scenarioId),
        );
        await refetchSpaces();
        toast.success("Fläche aktualisiert.");
      } catch (err) {
        console.error("Error updating space:", err);
        toast.error(
          formatErrorMessage(err) || "Aktualisierung fehlgeschlagen.",
          { duration: 8000 },
        );
      } finally {
        setSavingSingleSpace(false);
      }
    },
    [scenarioId, refetchSpaces],
  );

  const createSingleSpace = useCallback(
    async (elementId) => {
      if (!scenarioId) return;
      const el = elementsRef.current.find((e) => e.id === elementId);
      if (!el) return;
      if (el.spaceId) {
        toast.error("Diese Fläche existiert bereits im Backend.");
        return;
      }
      setSavingSingleSpace(true);
      try {
        const created = await spaceService.createSpace(
          elementToSpacePayload(el, scenarioId),
        );
        setElements((prev) =>
          prev.map((e) =>
            e.id === elementId
              ? { ...e, spaceId: created.id ?? created.data?.id }
              : e,
          ),
        );
        await refetchSpaces();
        toast.success("Fläche erstellt.");
      } catch (err) {
        console.error("Error creating space:", err);
        toast.error(formatErrorMessage(err) || "Erstellen fehlgeschlagen.", {
          duration: 8000,
        });
      } finally {
        setSavingSingleSpace(false);
      }
    },
    [scenarioId, refetchSpaces],
  );

  const deleteSingleSpace = useCallback(
    async (elementId) => {
      const el = elementsRef.current.find((e) => e.id === elementId);
      if (!el) return;
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        el.spaceId
          ? `Fläche "${el.name || el.space_code || el.spaceId}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`
          : "Element vom Canvas entfernen?",
      );
      if (!confirmed) return;
      setDeletingSingleSpace(true);
      try {
        if (el.spaceId) {
          await spaceService.deleteSpace(el.spaceId);
        }
        setElements((prev) => prev.filter((e) => e.id !== elementId));
        setSelectedElements([]);
        if (el.spaceId) await refetchSpaces();
        toast.success("Fläche gelöscht.");
      } catch (err) {
        console.error("Error deleting space:", err);
        toast.error(formatErrorMessage(err) || "Löschen fehlgeschlagen.", {
          duration: 8000,
        });
      } finally {
        setDeletingSingleSpace(false);
      }
    },
    [refetchSpaces],
  );

  const handlePasteSVG = useCallback(() => {
    if (!pasteSVGCode.trim()) {
      // eslint-disable-next-line no-alert
      alert("Please enter SVG code to import.");
      return;
    }
    if (importSVGCode) {
      importSVGCode(pasteSVGCode.trim(), { fitToView: fitImportToCanvas });
      setShowPasteSVGDialog(false);
      setPasteSVGCode("");
    }
  }, [
    pasteSVGCode,
    importSVGCode,
    fitImportToCanvas,
    setShowPasteSVGDialog,
    setPasteSVGCode,
  ]);

  const toggleFloatingPanel = useCallback(() => {
    setShowFloatingPanel((prev) => !prev);
  }, []);

  const _togglePanelCollapse = useCallback(() => {
    setIsRightPanelMinimized((prev) => !prev);
  }, []);

  const _handlePanelMouseDown = useCallback(
    (e) => {
      if (e.target.closest(".panel-drag-handle")) {
        const startX = e.clientX - panelPosition.x;
        const startY = e.clientY - panelPosition.y;
        const handleMouseMove = (ev) => {
          const newX = Math.max(
            0,
            Math.min(window.innerWidth - 320, ev.clientX - startX),
          );
          const newY = Math.max(
            0,
            Math.min(window.innerHeight - 100, ev.clientY - startY),
          );
          setPanelPosition({ x: newX, y: newY });
        };
        const handleMouseUp = () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }
    },
    [panelPosition],
  );

  const handleRightPanelMouseDown = useCallback(
    (e) => {
      if (e.target.closest(".right-panel-drag-handle")) {
        const startX = e.clientX - rightPanelPosition.x;
        const startY = e.clientY - rightPanelPosition.y;
        const handleMouseMove = (ev) => {
          const newX = Math.max(
            0,
            Math.min(window.innerWidth - 320, ev.clientX - startX),
          );
          const newY = Math.max(
            0,
            Math.min(window.innerHeight - 100, ev.clientY - startY),
          );
          setRightPanelPosition({ x: newX, y: newY });
        };
        const handleMouseUp = () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }
    },
    [rightPanelPosition],
  );

  const dockRightPanel = useCallback(() => {
    setIsRightPanelFloating(false);
    setIsRightPanelMinimized(false);
  }, []);
  const floatRightPanel = useCallback(() => {
    setIsRightPanelFloating(true);
    setRightPanelPosition({ x: window.innerWidth - 340, y: 120 });
  }, []);
  const toggleRightPanelMinimize = useCallback(() => {
    setIsRightPanelMinimized((prev) => !prev);
  }, []);
  const toggleRightPanel = useCallback(() => {
    setShowRightPanel((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPanelPosition((prev) => ({
        x: Math.min(prev.x, window.innerWidth - 320),
        y: Math.min(prev.y, window.innerHeight - 100),
      }));
      setRightPanelPosition((prev) => ({
        x: Math.min(prev.x, window.innerWidth - 320),
        y: Math.min(prev.y, window.innerHeight - 100),
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    if (isDragging || isResizing || isPanning) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, isResizing, isPanning, handleMouseMove, handleMouseUp]);

  useKeyboardShortcuts({
    selectedElements,
    deleteSelected,
    isCreatingPolygon,
    completePolygon,
    setSelectedElements,
    setTool,
    undo,
    redo,
    copyElements,
    pasteElements,
    duplicateElements,
    groupElements,
    copySVGToClipboard,
    setShowPasteSVGDialog,
    toggleFloatingPanel,
    toggleRightPanel,
  });

  useEffect(() => {
    if (history.length === 0) {
      setHistory([{ elements: [], timestamp: Date.now() }]);
      setHistoryIndex(0);
    }
  }, [history.length]);

  useEffect(() => {
    if (svgRef.current) {
      initializeGroups();
    }
  }, [initializeGroups]);

  // Update group visibility in SVG DOM
  useEffect(() => {
    if (!svgRef.current) return;
    Object.entries(groupVisibility).forEach(([groupId, visible]) => {
      const group = svgRef.current.querySelector(`g#${CSS.escape(groupId)}`);
      if (group) {
        group.style.display = visible ? "" : "none";
      }
    });
  }, [groupVisibility]);

  const renderGrid = useCallback(() => {
    if (!showGrid) return null;
    const lines = [];
    const {
      x: vbX,
      y: vbY,
      width: vbWidth,
      height: vbHeight,
    } = scenarioMetadata.viewBox;
    for (
      let i = Math.floor(vbX / GRID_SIZE) * GRID_SIZE;
      i <= vbX + vbWidth;
      i += GRID_SIZE
    ) {
      lines.push(
        <line
          key={`v${i}`}
          x1={i}
          y1={vbY}
          x2={i}
          y2={vbY + vbHeight}
          stroke="#e0e0e0"
          strokeWidth="0.5"
        />,
      );
    }
    for (
      let i = Math.floor(vbY / GRID_SIZE) * GRID_SIZE;
      i <= vbY + vbHeight;
      i += GRID_SIZE
    ) {
      lines.push(
        <line
          key={`h${i}`}
          x1={vbX}
          y1={i}
          x2={vbX + vbWidth}
          y2={i}
          stroke="#e0e0e0"
          strokeWidth="0.5"
        />,
      );
    }
    return <g className="grid">{lines}</g>;
  }, [showGrid, scenarioMetadata.viewBox]);

  const renderSelectionBox = useCallback(() => {
    if (!isSelectingBox || !selectionBoxStart || !selectionBoxEnd) return null;

    const x = Math.min(selectionBoxStart.x, selectionBoxEnd.x);
    const y = Math.min(selectionBoxStart.y, selectionBoxEnd.y);
    const width = Math.abs(selectionBoxEnd.x - selectionBoxStart.x);
    const height = Math.abs(selectionBoxEnd.y - selectionBoxStart.y);

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(33, 150, 243, 0.1)"
        stroke="#2196f3"
        strokeWidth="1"
        strokeDasharray="4 4"
        pointerEvents="none"
      />
    );
  }, [isSelectingBox, selectionBoxStart, selectionBoxEnd]);

  const renderBackground = useCallback(() => {
    if (!backgroundImage || !layerVisibility[LAYERS.BACKGROUND]) return null;
    const {
      x: vbX,
      y: vbY,
      width: vbWidth,
      height: vbHeight,
    } = scenarioMetadata.viewBox;
    // Background-only zoom: scale the background group about the viewBox
    // centre so the map zooms in place. Objects/GPS pins live in sibling
    // layers and are unaffected. Stamped as data-background-zoom so the
    // factor survives serialization into svg_content.
    const bgZoom = Number(scenarioMetadata.backgroundZoom) || 1;
    const bgCx = vbX + vbWidth / 2;
    const bgCy = vbY + vbHeight / 2;
    const bgTransform =
      bgZoom !== 1
        ? `translate(${bgCx} ${bgCy}) scale(${bgZoom}) translate(${-bgCx} ${-bgCy})`
        : undefined;
    try {
      if (backgroundImage.type === "svg") {
        return (
          <g
            key="background-svg"
            transform={bgTransform}
            data-background-zoom={bgZoom}
          >
            <foreignObject
              x={vbX}
              y={vbY}
              width={vbWidth}
              height={vbHeight}
              style={{ overflow: "hidden" }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: backgroundImage.data }}
                style={{
                  width: "100%",
                  height: "100%",
                  opacity: 0.8,
                  pointerEvents: "none",
                }}
              />
            </foreignObject>
          </g>
        );
      }
      const imageAspect =
        backgroundImage.originalWidth / backgroundImage.originalHeight;
      const viewBoxAspect = vbWidth / vbHeight;
      let fitWidth, fitHeight, offsetX, offsetY;
      if (imageAspect > viewBoxAspect) {
        fitWidth = vbWidth;
        fitHeight = vbWidth / imageAspect;
        offsetX = vbX;
        offsetY = vbY + (vbHeight - fitHeight) / 2;
      } else {
        fitHeight = vbHeight;
        fitWidth = vbHeight * imageAspect;
        offsetX = vbX + (vbWidth - fitWidth) / 2;
        offsetY = vbY;
      }
      return (
        <g
          key="background-image"
          transform={bgTransform}
          data-background-zoom={bgZoom}
        >
          <rect
            x={vbX}
            y={vbY}
            width={vbWidth}
            height={vbHeight}
            fill="#f8f9fa"
            opacity={0.5}
          />
          <image
            x={offsetX}
            y={offsetY}
            width={fitWidth}
            height={fitHeight}
            href={backgroundImage.data}
            opacity="0.9"
            preserveAspectRatio="xMidYMid meet"
            crossOrigin="anonymous"
            style={{ pointerEvents: "none" }}
          />
          <g opacity="0.8" style={{ pointerEvents: "none" }}>
            <rect
              x={vbX + 10}
              y={vbY + vbHeight - 40}
              width={Math.min(300, vbWidth - 20)}
              height={30}
              fill="rgba(0,0,0,0.7)"
              rx="4"
            />
            <text
              x={vbX + 15}
              y={vbY + vbHeight - 25}
              fontSize="10"
              fill="white"
              fontFamily="Arial, sans-serif"
            >
              {backgroundImage.filename || "Background Image"}
            </text>
            <text
              x={vbX + 15}
              y={vbY + vbHeight - 15}
              fontSize="8"
              fill="#ccc"
              fontFamily="Arial, sans-serif"
            >
              {backgroundImage.originalWidth}×{backgroundImage.originalHeight}px
              • {backgroundImage.source} • {backgroundImage.type.toUpperCase()}
            </text>
          </g>
        </g>
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error rendering background:", error);
      return (
        <g key="background-error">
          <rect
            x={vbX}
            y={vbY}
            width={vbWidth}
            height={vbHeight}
            fill="#fee"
            opacity={0.8}
          />
          <text
            x={vbX + vbWidth / 2}
            y={vbY + vbHeight / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fill="#d32f2f"
            style={{ pointerEvents: "none" }}
          >
            Background Load Error
          </text>
        </g>
      );
    }
  }, [
    backgroundImage,
    layerVisibility,
    scenarioMetadata.viewBox,
    scenarioMetadata.backgroundZoom,
  ]);

  const backgroundNode = useMemo(() => renderBackground(), [renderBackground]);
  const gridNode = useMemo(() => renderGrid(), [renderGrid]);

  const renderResizeHandles = useCallback(
    (element) => {
      if (
        !selectedElements.find((el) => el.id === element.id) ||
        tool !== "select"
      )
        return null;
      const handles = [];
      const circularTypes = [
        "circle",
        "tree",
        "trash",
        "fountain_drink",
        "lighting",
        "info_board",
        "fountain_water",
      ];
      const rectangularTypes = [
        "rect",
        "stage",
        "seating",
        "parking",
        "toilet",
        "fence",
        "entrance",
      ];
      try {
        if (rectangularTypes.includes(element.type)) {
          const { x, y, width, height } = element;
          const handlePositions = [
            { id: "nw", x, y },
            { id: "ne", x: x + width, y },
            { id: "sw", x, y: y + height },
            { id: "se", x: x + width, y: y + height },
          ];
          handlePositions.forEach((handle) => {
            handles.push(
              <rect
                key={`handle-${handle.id}`}
                className="resize-handle"
                x={handle.x - 4}
                y={handle.y - 4}
                width={8}
                height={8}
                fill="#2196f3"
                stroke="#fff"
                strokeWidth="1"
                style={{ cursor: `${handle.id}-resize` }}
              />,
            );
          });
        } else if (circularTypes.includes(element.type)) {
          handles.push(
            <circle
              key="handle-resize"
              className="resize-handle"
              cx={element.cx + element.r}
              cy={element.cy}
              r={4}
              fill="#2196f3"
              stroke="#fff"
              strokeWidth="1"
              style={{ cursor: "ew-resize" }}
            />,
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error rendering resize handles:", error);
      }
      return <g>{handles}</g>;
    },
    [selectedElements, tool],
  );

  const renderElement = useCallback(
    (element) => {
      if (!element?.visible) return null;
      const isSelected = selectedElements.find((el) => el.id === element.id);
      const opacity = element.locked ? 0.6 : 1;
      let rotateCx = element.cx || element.x || 0;
      let rotateCy = element.cy || element.y || 0;
      if (
        element.type === "polygon" &&
        Array.isArray(element.points) &&
        element.points.length > 0
      ) {
        const xs = element.points.map((p) => p.x || p.lng || 0);
        const ys = element.points.map((p) => p.y || p.lat || 0);
        rotateCx = (Math.min(...xs) + Math.max(...xs)) / 2;
        rotateCy = (Math.min(...ys) + Math.max(...ys)) / 2;
      } else if (element.type === "rect") {
        rotateCx = (element.x || 0) + (element.width || 0) / 2;
        rotateCy = (element.y || 0) + (element.height || 0) / 2;
      }
      const transform = element.rotation
        ? `rotate(${element.rotation} ${rotateCx} ${rotateCy})`
        : "";
      try {
        switch (element.type) {
          case "rect":
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <rect
                  x={element.x}
                  y={element.y}
                  width={element.width}
                  height={element.height}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                {element.label && (
                  <text
                    x={element.x + element.width / 2}
                    y={element.y + element.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="12"
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                  </text>
                )}
                {isSelected && (
                  <rect
                    className="selection-indicator"
                    x={element.x - 2}
                    y={element.y - 2}
                    width={element.width + 4}
                    height={element.height + 4}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {renderResizeHandles(element)}
              </g>
            );
          case "circle":
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <circle
                  cx={element.cx}
                  cy={element.cy}
                  r={element.r}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                {element.label && (
                  <text
                    x={element.cx}
                    y={element.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="12"
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                  </text>
                )}
                {isSelected && (
                  <circle
                    className="selection-indicator"
                    cx={element.cx}
                    cy={element.cy}
                    r={element.r + 3}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {renderResizeHandles(element)}
              </g>
            );
          case "tree":
          case "trash":
          case "fountain_drink":
          case "lighting":
          case "info_board": {
            const baseGeom = element.baseGeometry;
            const radius = element.r;
            const scale = baseGeom?.radius
              ? radius / baseGeom.radius
              : (element.scale ?? 1);
            const labelOffset = baseGeom?.labelOffset
              ? baseGeom.labelOffset * scale
              : Math.max(12, radius * 0.6);
            const trunkWidth = baseGeom?.trunkWidth
              ? baseGeom.trunkWidth * scale
              : Math.max(4, radius * 0.35);
            const trunkHeight = baseGeom?.trunkHeight
              ? baseGeom.trunkHeight * scale
              : Math.max(8, radius * 1.1);
            const lampPoleWidth = baseGeom?.poleWidth
              ? baseGeom.poleWidth * scale
              : Math.max(2, radius * 0.12);
            const lampPoleHeight = baseGeom?.poleHeight
              ? baseGeom.poleHeight * scale
              : Math.max(12, radius * 1.6);
            const lampRadius = baseGeom?.lampRadius
              ? baseGeom.lampRadius * scale
              : Math.max(6, radius * 0.55);
            const trashWidth = baseGeom?.trashWidth
              ? baseGeom.trashWidth * scale
              : Math.max(8, radius * 0.9);
            const trashHeight = baseGeom?.trashHeight
              ? baseGeom.trashHeight * scale
              : Math.max(12, radius * 1.4);
            const infoWidth = baseGeom?.boardWidth
              ? baseGeom.boardWidth * scale
              : Math.max(16, radius * 1.6);
            const infoHeight = baseGeom?.boardHeight
              ? baseGeom.boardHeight * scale
              : Math.max(12, radius * 1.1);
            const cupTopWidth = baseGeom?.cupTopWidth
              ? baseGeom.cupTopWidth * scale
              : Math.max(12, radius * 1.4);
            const cupBottomWidth = baseGeom?.cupBottomWidth
              ? baseGeom.cupBottomWidth * scale
              : Math.max(8, radius);
            const cupHeight = baseGeom?.cupHeight
              ? baseGeom.cupHeight * scale
              : Math.max(12, radius * 1.8);
            const cupTopY = element.cy - radius * 0.6;
            const cupBottomY = cupTopY + cupHeight;
            const cupPath = `M ${element.cx - cupTopWidth / 2} ${cupTopY} L ${
              element.cx + cupTopWidth / 2
            } ${cupTopY} L ${element.cx + cupBottomWidth / 2} ${cupBottomY} L ${
              element.cx - cupBottomWidth / 2
            } ${cupBottomY} Z`;

            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <circle
                  cx={element.cx}
                  cy={element.cy}
                  r={radius}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                {element.type === "tree" && (
                  <rect
                    x={element.cx - trunkWidth / 2}
                    y={element.cy + radius - trunkHeight}
                    width={trunkWidth}
                    height={trunkHeight}
                    fill="#8d6e63"
                  />
                )}
                {element.type === "lighting" && (
                  <>
                    <rect
                      x={element.cx - lampPoleWidth / 2}
                      y={element.cy - radius - lampPoleHeight}
                      width={lampPoleWidth}
                      height={lampPoleHeight}
                      fill="#424242"
                    />
                    <circle
                      cx={element.cx}
                      cy={element.cy - radius - lampPoleHeight - lampRadius}
                      r={lampRadius}
                      fill="#fff9c4"
                      stroke="#f57f17"
                      strokeWidth={Math.max(1, lampRadius * 0.12)}
                      opacity={0.85}
                    />
                  </>
                )}
                {element.type === "trash" && (
                  <rect
                    x={element.cx - trashWidth / 2}
                    y={element.cy - trashHeight / 2}
                    width={trashWidth}
                    height={trashHeight}
                    fill={element.fill}
                    stroke={element.stroke}
                    strokeWidth={Math.max(1, radius * 0.08)}
                  />
                )}
                {element.type === "fountain_drink" && (
                  <path
                    d={cupPath}
                    fill={element.fill}
                    stroke={element.stroke}
                    strokeWidth={Math.max(1, radius * 0.08)}
                  />
                )}
                {element.type === "info_board" && (
                  <rect
                    x={element.cx - infoWidth / 2}
                    y={element.cy - infoHeight / 2}
                    width={infoWidth}
                    height={infoHeight}
                    fill={element.fill}
                    stroke={element.stroke}
                    strokeWidth={Math.max(1, radius * 0.08)}
                  />
                )}
                {element.label && (
                  <text
                    x={element.cx}
                    y={element.cy + radius + labelOffset}
                    textAnchor="middle"
                    fontSize={Math.max(10, radius * 0.45)}
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                  </text>
                )}
                {isSelected && (
                  <circle
                    className="selection-indicator"
                    cx={element.cx}
                    cy={element.cy}
                    r={radius + 3}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {renderResizeHandles(element)}
              </g>
            );
          }
          case "stage":
          case "seating":
          case "parking":
          case "toilet":
          case "fence":
          case "entrance":
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <rect
                  x={element.x}
                  y={element.y}
                  width={element.width}
                  height={element.height}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                {element.type === "stage" && (
                  <>
                    <rect
                      x={element.x + 5}
                      y={element.y + 5}
                      width={element.width - 10}
                      height={element.height - 10}
                      fill="none"
                      stroke={element.stroke}
                      strokeWidth={1}
                    />
                    <text
                      x={element.x + element.width / 2}
                      y={element.y + element.height / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="8"
                      fill="#fff"
                      style={{ pointerEvents: "none" }}
                    >
                      STAGE
                    </text>
                  </>
                )}
                {element.type === "seating" && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <rect
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        x={element.x + 5 + i * 15}
                        y={element.y + 8}
                        width={12}
                        height={element.height - 16}
                        fill="none"
                        stroke={element.stroke}
                        strokeWidth={1}
                      />
                    ))}
                  </>
                )}
                {element.type === "parking" && (
                  <>
                    <line
                      x1={element.x + element.width / 3}
                      y1={element.y}
                      x2={element.x + element.width / 3}
                      y2={element.y + element.height}
                      stroke="#fff"
                      strokeWidth={1}
                    />
                    <line
                      x1={element.x + (2 * element.width) / 3}
                      y1={element.y}
                      x2={element.x + (2 * element.width) / 3}
                      y2={element.y + element.height}
                      stroke="#fff"
                      strokeWidth={1}
                    />
                  </>
                )}
                {element.type === "toilet" && (
                  <text
                    x={element.x + element.width / 2}
                    y={element.y + element.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="8"
                    fill="#fff"
                    style={{ pointerEvents: "none" }}
                  >
                    WC
                  </text>
                )}
                {element.type === "fence" && (
                  <>
                    {[...Array(5)].map((_, i) => (
                      <line
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        x1={element.x + 2 + i * (element.width / 5)}
                        y1={element.y + 5}
                        x2={element.x + 2 + i * (element.width / 5)}
                        y2={element.y + element.height - 5}
                        stroke={element.stroke}
                        strokeWidth={2}
                      />
                    ))}
                  </>
                )}
                {element.type === "entrance" && (
                  <>
                    <rect
                      x={element.x + element.width / 4}
                      y={element.y + 2}
                      width={element.width / 2}
                      height={element.height - 4}
                      fill="none"
                      stroke={element.stroke}
                      strokeWidth={2}
                      strokeDasharray="3,3"
                    />
                    <circle
                      cx={element.x + element.width - 8}
                      cy={element.y + element.height / 2}
                      r={2}
                      fill={element.stroke}
                    />
                  </>
                )}
                {element.label && (
                  <text
                    x={element.x + element.width / 2}
                    y={element.y + element.height + 15}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                  </text>
                )}
                {isSelected && (
                  <rect
                    className="selection-indicator"
                    x={element.x - 2}
                    y={element.y - 2}
                    width={element.width + 4}
                    height={element.height + 4}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {renderResizeHandles(element)}
              </g>
            );
          case "fountain_water":
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <circle
                  cx={element.cx}
                  cy={element.cy}
                  r={element.r}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                <circle
                  cx={element.cx}
                  cy={element.cy}
                  r={element.r - 8}
                  fill="none"
                  stroke={element.stroke}
                  strokeWidth={1}
                />
                {[...Array(6)].map((_, i) => {
                  const angle = (i * Math.PI * 2) / 6;
                  const x = element.cx + Math.cos(angle) * (element.r - 12);
                  const y = element.cy + Math.sin(angle) * (element.r - 12);
                  return (
                    <circle // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      cx={x}
                      cy={y}
                      r={2}
                      fill="#b3e5fc"
                      opacity={0.8}
                    />
                  );
                })}
                {element.label && (
                  <text
                    x={element.cx}
                    y={element.cy + element.r + 20}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                  </text>
                )}
                {isSelected && (
                  <circle
                    className="selection-indicator"
                    cx={element.cx}
                    cy={element.cy}
                    r={element.r + 3}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {renderResizeHandles(element)}
              </g>
            );
          case "pin": {
            const isReferencePoint = referencePoints.includes(element.id);
            const pinFill = isReferencePoint ? "#4caf50" : element.fill;
            const pinStroke = isReferencePoint ? "#2e7d32" : element.stroke;
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <path
                  d={`M ${element.x + 10} ${element.y} 
                    C ${element.x + 15} ${element.y}, ${element.x + 20} ${element.y + 5}, ${element.x + 20} ${element.y + 10}
                    C ${element.x + 20} ${element.y + 15}, ${element.x + 15} ${element.y + 20}, ${element.x + 10} ${element.y + 20}
                    C ${element.x + 5} ${element.y + 20}, ${element.x} ${element.y + 15}, ${element.x} ${element.y + 10}
                    C ${element.x} ${element.y + 5}, ${element.x + 5} ${element.y}, ${element.x + 10} ${element.y} Z
                    M ${element.x + 10} ${element.y + 20} L ${element.x + 10} ${element.y + 30} Z`}
                  fill={pinFill}
                  stroke={pinStroke}
                  strokeWidth={element.strokeWidth}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                <circle
                  cx={element.x + 10}
                  cy={element.y + 10}
                  r={4}
                  fill="#fff"
                />
                {isReferencePoint && (
                  <>
                    <circle
                      cx={element.x + 10}
                      cy={element.y + 10}
                      r={8}
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth={2}
                      strokeDasharray="2,2"
                      opacity={0.8}
                    />
                    <text
                      x={element.x + 10}
                      y={element.y + 10}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="8"
                      fill="#4caf50"
                      fontWeight="bold"
                      style={{ pointerEvents: "none" }}
                    >
                      GPS
                    </text>
                  </>
                )}
                {element.label && (
                  <text
                    x={element.x + 25}
                    y={element.y + 15}
                    fontSize="10"
                    fill="#666"
                    style={{ pointerEvents: "none" }}
                  >
                    {element.label}
                    {element.gps && (
                      <tspan
                        x={element.x + 25}
                        dy="12"
                        fontSize="8"
                        fill="#888"
                      >
                        {element.gps.latitude.toFixed(6)},{" "}
                        {element.gps.longitude.toFixed(6)}
                      </tspan>
                    )}
                  </text>
                )}
                {isSelected && (
                  <rect
                    className="selection-indicator"
                    x={element.x - 2}
                    y={element.y - 2}
                    width={24}
                    height={34}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
              </g>
            );
          }
          case "polygon": {
            if (!element.points || element.points.length < 3) return null;
            // Handle both SVG (x,y) and OSM (lng,lat) coordinate systems
            const coordSystem =
              element.coordinateSystem ||
              scenarioMetadata.coordinateSystem ||
              "svg";
            // Convert OSM coordinates to pixel for SVG rendering
            let pointsStr;
            if (coordSystem === "osm" && scenarioMetadata.osmCenter) {
              // Convert lng/lat to pixel coordinates for rendering
              const pixelPoints = element.points.map((p) => {
                const lng = p.lng || p.x || 0;
                const lat = p.lat || p.y || 0;
                return lngLatToPixel(
                  lng,
                  lat,
                  scenarioMetadata.osmCenter,
                  scenarioMetadata.osmZoom || 15,
                );
              });
              pointsStr = pixelPoints.map((p) => `${p.x},${p.y}`).join(" ");
            } else {
              // SVG mode: use x,y directly
              pointsStr = element.points
                .map((p) => `${p.x || p.lng || 0},${p.y || p.lat || 0}`)
                .join(" ");
            }
            const isActivePoly = activePolygon?.id === element.id;
            // Only show polygons in selected group
            if (element.groupId && element.groupId !== selectedGroupId) {
              return null;
            }
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <polygon
                  points={pointsStr}
                  fill={element.fill}
                  stroke={element.stroke}
                  strokeWidth={element.strokeWidth}
                  fillOpacity={0.7}
                  data-coordinate-system={coordSystem}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  onDoubleClick={(e) => {
                    if (!element.locked && tool === "edit") {
                      e.stopPropagation();
                      setActivePolygon(element);
                    }
                  }}
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag" || tool === "edit"
                        ? "pointer"
                        : "crosshair",
                  }}
                />
                {(element.label || element.polygonId) &&
                  (() => {
                    // Calculate center point based on coordinate system
                    const centerX =
                      coordSystem === "osm"
                        ? element.points.reduce(
                            (sum, p) => sum + (p.lng || p.x || 0),
                            0,
                          ) / element.points.length
                        : element.points.reduce(
                            (sum, p) => sum + (p.x || p.lng || 0),
                            0,
                          ) / element.points.length;
                    const centerY =
                      coordSystem === "osm"
                        ? element.points.reduce(
                            (sum, p) => sum + (p.lat || p.y || 0),
                            0,
                          ) / element.points.length
                        : element.points.reduce(
                            (sum, p) => sum + (p.y || p.lat || 0),
                            0,
                          ) / element.points.length;
                    // Convert to pixel for display if OSM
                    const displayPoint =
                      coordSystem === "osm" && scenarioMetadata.osmCenter
                        ? lngLatToPixel(
                            centerX,
                            centerY,
                            scenarioMetadata.osmCenter,
                            scenarioMetadata.osmZoom || 15,
                          )
                        : { x: centerX, y: centerY };

                    return (
                      <text
                        x={displayPoint.x}
                        y={displayPoint.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="11"
                        fill="#1f2937"
                        fontWeight="600"
                        style={{
                          pointerEvents: "none",
                          paintOrder: "stroke",
                        }}
                        stroke="#ffffff"
                        strokeWidth="3"
                        strokeOpacity="0.85"
                      >
                        {element.label || element.polygonId}
                      </text>
                    );
                  })()}
                {(isSelected || isActivePoly) && (
                  <polygon
                    className="selection-indicator"
                    points={pointsStr}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="3"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
                {(isSelected || isActivePoly) &&
                  tool === "edit" &&
                  element.points.map((point, index) => {
                    const isHovered = hoverVertexIndex === index;
                    const isDragging = draggingVertexIndex === index;
                    // Convert point to pixel coordinates for display
                    const displayPoint =
                      coordSystem === "osm" && scenarioMetadata.osmCenter
                        ? lngLatToPixel(
                            point.lng || point.x || 0,
                            point.lat || point.y || 0,
                            scenarioMetadata.osmCenter,
                            scenarioMetadata.osmZoom || 15,
                          )
                        : {
                            x: point.x || point.lng || 0,
                            y: point.y || point.lat || 0,
                          };

                    return (
                      <circle
                        // eslint-disable-next-line react/no-array-index-key
                        key={`vertex-${index}`}
                        className="vertex-handle"
                        cx={displayPoint.x}
                        cy={displayPoint.y}
                        r={isHovered || isDragging ? 6 : 5}
                        fill={
                          isDragging
                            ? "#ff5722"
                            : isHovered
                              ? "#ff9800"
                              : "#2196f3"
                        }
                        stroke="#fff"
                        strokeWidth="2"
                        style={{
                          cursor: "pointer",
                          pointerEvents: "all",
                        }}
                        onMouseEnter={() => setHoverVertexIndex(index)}
                        onMouseLeave={() => setHoverVertexIndex(-1)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (element.points.length > 3) {
                            deleteVertexFromPolygon(element.id, index);
                          }
                        }}
                      />
                    );
                  })}
                {/* Edge hover indicator for adding vertices */}
                {isActivePoly &&
                  tool === "edit" &&
                  hoverEdgeIndex !== null &&
                  hoverEdgeIndex >= 0 &&
                  (() => {
                    const p1 = element.points[hoverEdgeIndex];
                    const p2 =
                      element.points[
                        (hoverEdgeIndex + 1) % element.points.length
                      ];
                    const coordSystem =
                      element.coordinateSystem ||
                      scenarioMetadata.coordinateSystem ||
                      "svg";
                    // Calculate midpoint based on coordinate system
                    const midX =
                      coordSystem === "osm"
                        ? ((p1.lng || p1.x) + (p2.lng || p2.x)) / 2
                        : ((p1.x || p1.lng) + (p2.x || p2.lng)) / 2;
                    const midY =
                      coordSystem === "osm"
                        ? ((p1.lat || p1.y) + (p2.lat || p2.y)) / 2
                        : ((p1.y || p1.lat) + (p2.y || p2.lat)) / 2;
                    // Convert to pixel for display if OSM
                    const displayPoint =
                      coordSystem === "osm" && scenarioMetadata.osmCenter
                        ? lngLatToPixel(
                            midX,
                            midY,
                            scenarioMetadata.osmCenter,
                            scenarioMetadata.osmZoom || 15,
                          )
                        : { x: midX, y: midY };

                    return (
                      <circle
                        cx={displayPoint.x}
                        cy={displayPoint.y}
                        r={5}
                        fill="#4caf50"
                        stroke="#fff"
                        strokeWidth="1"
                        style={{ pointerEvents: "all", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const midPoint =
                            coordSystem === "osm"
                              ? { lng: midX, lat: midY }
                              : { x: midX, y: midY };
                          addVertexToPolygon(
                            element.id,
                            hoverEdgeIndex,
                            midPoint,
                          );
                        }}
                      />
                    );
                  })()}
              </g>
            );
          }
          case "text":
            return (
              <g key={element.id} transform={transform} opacity={opacity}>
                <text
                  x={element.x}
                  y={element.y}
                  fontSize={element.fontSize}
                  fill={element.fill}
                  fontFamily={element.fontFamily}
                  onClick={(e) =>
                    !element.locked && handleElementClick(e, element)
                  }
                  style={{
                    cursor: element.locked
                      ? "not-allowed"
                      : tool === "select" || tool === "drag"
                        ? "pointer"
                        : "crosshair",
                  }}
                >
                  {element.text}
                </text>
                {isSelected && (
                  <rect
                    className="selection-indicator"
                    x={element.x - 5}
                    y={element.y - element.fontSize}
                    width={
                      (element.text?.length || 0) * (element.fontSize * 0.6) +
                      10
                    }
                    height={element.fontSize + 5}
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    style={{ pointerEvents: "none" }}
                  />
                )}
              </g>
            );
          default:
            return null;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error rendering element:", error);
        return null;
      }
    },
    [
      selectedElements,
      tool,
      handleElementClick,
      renderResizeHandles,
      referencePoints,
    ],
  );

  const renderElementsByLayer = useCallback(
    (layerId) => {
      if (!layerVisibility[layerId]) return null;
      return getElementsForLayer(layerId).map((el) => (
        <MemoElement key={el.id} element={el} render={renderElement} />
      ));
    },
    [layerVisibility, getElementsForLayer, renderElement],
  );

  return (
    <>
      <GlobalAdminHeader
        title="Flächen (Spaces)"
        subtitle={
          scenarioId
            ? `Buchbare Flächen für Szenario ${scenarioId} verwalten`
            : "Wähle ein Szenario, um Flächen zu bearbeiten und zu speichern."
        }
      />
      {!scenarioId && (
        <div className="mx-4 mt-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
          Bitte wähle zuerst ein Szenario aus, um Flächen zu zeichnen und zu
          speichern.
        </div>
      )}
      <div className="h-screen bg-gray-100 dark:bg-slate-900 flex overflow-hidden p-4 gap-3">
        <div className="flex bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-y-auto">
          {/* Toolbar */}
          <Toolbar
            tool={tool}
            setTool={setTool}
            showParkObjectsDropdown={showParkObjectsDropdown}
            setShowParkObjectsDropdown={setShowParkObjectsDropdown}
            rightPanel={rightPanel}
            setRightPanel={setRightPanel}
            toggleRightPanel={toggleRightPanel}
            showRightPanel={showRightPanel}
            deleteSelected={deleteSelected}
            hasSelection={hasSelection}
            showGrid={showGrid}
            setShowGrid={setShowGrid}
            snapToGrid={snapToGrid}
            setSnapToGrid={setSnapToGrid}
          />
          {/* Main Canvas */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden gap-3">
            {/* Top Controls */}
            <TopControls
              scenarioMetadata={scenarioMetadata}
              setShowScenarioSettings={setShowScenarioSettings}
              backgroundImage={backgroundImage}
              currentLayer={currentLayer}
              canUndo={canUndo}
              canRedo={canRedo}
              undo={undo}
              redo={redo}
              copyElements={copyElements}
              pasteElements={pasteElements}
              hasSelection={hasSelection}
              clipboard={clipboard}
              canAlign={canAlign}
              alignElements={alignElements}
              changeZOrder={changeZOrder}
              svgZoom={effectiveZoom}
              setSvgZoom={setSvgZoom}
              handleButtonZoom={handleButtonZoom}
              handleFitToView={handleFitToView}
              groups={groups}
              selectedGroupId={selectedGroupId}
              selectGroup={selectGroup}
              setShowGroupManager={setShowGroupManager}
              selectedElements={selectedElements}
              copySVGToClipboard={copySVGToClipboard}
              setShowPasteSVGDialog={setShowPasteSVGDialog}
              exportSVG={exportSVG}
              isCreatingPolygon={isCreatingPolygon}
              polygonPoints={polygonPoints}
              completePolygon={completePolygon}
              tool={tool}
              availableScenarios={availableScenarios}
              scenarioId={scenarioId}
              spacesLoading={spacesLoading}
              onScenarioChange={(id) => {
                if (id) navigate(`/admin/scenario-editor/${id}`);
                else navigate(`/admin/scenario-editor`);
              }}
            />
            {scenarioId && spaces.length > 0 && (
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                    Geladene Flächen ({spaces.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    {spacesLoading && (
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        lädt…
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={openSpaceModalForAdd}
                      className="text-xs px-2.5 py-1 rounded bg-primary-color text-white hover:opacity-90"
                    >
                      + Hinzufügen
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {spaces.map((space) => {
                    const linked = elements.find(
                      (el) => el.spaceId === space.id,
                    );
                    const isSelected =
                      linked &&
                      selectedElements.some((s) => s.id === linked.id);
                    return (
                      <button
                        key={space.id}
                        type="button"
                        onClick={() => {
                          if (linked) {
                            setSelectedElements([linked]);
                            setRightPanel("properties");
                            setShowRightPanel(true);
                          }
                        }}
                        disabled={!linked}
                        className={`text-left px-2 py-1.5 rounded border text-xs transition-colors ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                            : "border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={
                          linked
                            ? "Klicken für Eigenschaften"
                            : "Nicht auf dem Canvas gefunden"
                        }
                      >
                        <div className="font-medium text-gray-900 dark:text-slate-100">
                          {space.space_code || "—"}
                        </div>
                        <div className="text-gray-600 dark:text-slate-400 truncate max-w-[160px]">
                          {space.name || "(ohne Name)"}
                        </div>
                        <div className="text-gray-400 dark:text-slate-500">
                          {space.space_type}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Canvas */}
            <div className="flex-1 overflow-hidden relative min-h-0">
              {isScenarioLoading && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-slate-900/70 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-3 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg px-6 py-5">
                    <Loader2 className="w-7 h-7 text-primary-color animate-spin" />
                    <p className="text-sm font-medium text-gray-700 dark:text-slate-200">
                      Szenario wird geladen …
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      Flächen und Hintergrundplan werden geladen
                    </p>
                  </div>
                </div>
              )}
              <div
                ref={canvasContainerRef}
                className="w-full h-full overflow-auto  bg-white dark:bg-slate-800 shadow-lg dark:border dark:border-slate-700 min-h-0 "
                style={{
                  cursor:
                    tool === "drag"
                      ? isPanning
                        ? "grabbing"
                        : "grab"
                      : tool === "edit"
                        ? "crosshair"
                        : tool !== "select"
                          ? "crosshair"
                          : "default",
                  userSelect: isPanning ? "none" : "auto",
                }}
                onWheel={handleWheel}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <svg
                    ref={svgRef}
                    viewBox={`${scenarioMetadata.viewBox.x} ${scenarioMetadata.viewBox.y} ${scenarioMetadata.viewBox.width} ${scenarioMetadata.viewBox.height}`}
                    className="block"
                    style={{
                      width: `${100 * effectiveZoom}%`,
                      height: `${100 * effectiveZoom}%`,
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onDoubleClick={
                      isCreatingPolygon ? completePolygon : undefined
                    }
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {backgroundNode}
                    {gridNode}
                    {renderSelectionBox()}
                    {renderElementsByLayer(LAYERS.BACKGROUND)}
                    {renderElementsByLayer(LAYERS.MAIN)}
                    {renderElementsByLayer(LAYERS.OVERLAY)}
                    {isCreatingPolygon && polygonPoints.length > 0 && (
                      <g>
                        {polygonPoints.map((point, index) => (
                          <circle
                            // eslint-disable-next-line react/no-array-index-key
                            key={`preview-point-${index}`}
                            cx={point.x}
                            cy={point.y}
                            r={4}
                            fill="#2196f3"
                            stroke="#fff"
                            strokeWidth="2"
                          />
                        ))}
                        {polygonPoints.length > 1 && (
                          <polyline
                            points={polygonPoints
                              .map((p) => `${p.x},${p.y}`)
                              .join(" ")}
                            fill="none"
                            stroke={
                              currentLayer === LAYERS.BACKGROUND
                                ? "#4caf50"
                                : currentLayer === LAYERS.OVERLAY
                                  ? "#9c27b0"
                                  : "#f57c00"
                            }
                            strokeWidth="2"
                            strokeDasharray="5,5"
                          />
                        )}
                        {polygonPoints.length >= 3 && (
                          <polygon
                            points={polygonPoints
                              .map((p) => `${p.x},${p.y}`)
                              .join(" ")}
                            fill={
                              currentLayer === LAYERS.BACKGROUND
                                ? "#e8f5e8"
                                : currentLayer === LAYERS.OVERLAY
                                  ? "#f3e5f5"
                                  : "#fff3e0"
                            }
                            stroke={
                              currentLayer === LAYERS.BACKGROUND
                                ? "#4caf50"
                                : currentLayer === LAYERS.OVERLAY
                                  ? "#9c27b0"
                                  : "#f57c00"
                            }
                            strokeWidth="2"
                            fillOpacity="0.3"
                            strokeDasharray="3,3"
                            style={{ pointerEvents: "none" }}
                          />
                        )}
                        <text
                          x={
                            polygonPoints.length > 0
                              ? polygonPoints[polygonPoints.length - 1].x + 10
                              : 0
                          }
                          y={
                            polygonPoints.length > 0
                              ? polygonPoints[polygonPoints.length - 1].y - 10
                              : 0
                          }
                          fontSize="10"
                          fill="#2196f3"
                          style={{ pointerEvents: "none" }}
                        >
                          {polygonPoints.length >= 3
                            ? "Double-click to finish"
                            : `${polygonPoints.length} points`}
                        </text>
                      </g>
                    )}
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Panel */}
        <RightPanel
          rightPanel={rightPanel}
          setRightPanel={setRightPanel}
          showRightPanel={showRightPanel}
          isRightPanelFloating={isRightPanelFloating}
          isRightPanelMinimized={isRightPanelMinimized}
          rightPanelPosition={rightPanelPosition}
          handleRightPanelMouseDown={handleRightPanelMouseDown}
          toggleRightPanelMinimize={toggleRightPanelMinimize}
          dockRightPanel={dockRightPanel}
          floatRightPanel={floatRightPanel}
          toggleRightPanel={toggleRightPanel}
          selectedElement={selectedElement}
          updateElement={updateElement}
          rotateElement={rotateElement}
          toggleElementLock={toggleElementLock}
          toggleElementVisibility={toggleElementVisibility}
          elements={elements}
          selectedElements={selectedElements}
          setSelectedElements={setSelectedElements}
          currentLayer={currentLayer}
          setCurrentLayer={setCurrentLayer}
          layerVisibility={layerVisibility}
          toggleLayerVisibility={toggleLayerVisibility}
          getElementsForLayer={getElementsForLayer}
          backgroundImage={backgroundImage}
          backgroundImageURL={backgroundImageURL}
          setBackgroundImageURL={setBackgroundImageURL}
          importBackgroundFromURL={importBackgroundFromURL}
          importBackgroundImage={importBackgroundImage}
          backgroundInputRef={backgroundInputRef}
          setBackgroundImage={setBackgroundImage}
          scenarioMetadata={scenarioMetadata}
          referencePoints={referencePoints}
          toggleReferencePoint={toggleReferencePoint}
          updatePinGPS={updatePinGPS}
          openSpaceModalForEdit={openSpaceModalForEdit}
          onSaveAll={saveAll}
          isSaving={isSavingSpaces}
          canSave={Boolean(scenarioId) && !spacesLoading}
          onDeleteSpace={deleteSingleSpace}
          isDeleting={deletingSingleSpace}
          useCases={useCases}
        />
        {/* Modals */}
        <ScenarioSettingsModal
          showScenarioSettings={showScenarioSettings}
          setShowScenarioSettings={setShowScenarioSettings}
          scenarioMetadata={scenarioMetadata}
          setScenarioMetadata={setScenarioMetadata}
          referencePoints={referencePoints}
          elements={elements}
          toggleReferencePoint={toggleReferencePoint}
        />
        <PasteSVGDialogModal
          showPasteSVGDialog={showPasteSVGDialog}
          setShowPasteSVGDialog={setShowPasteSVGDialog}
          pasteSVGCode={pasteSVGCode}
          setPasteSVGCode={setPasteSVGCode}
          currentLayer={currentLayer}
          handlePasteSVG={handlePasteSVG}
          fitToCanvas={fitImportToCanvas}
          setFitToCanvas={setFitImportToCanvas}
        />
        <GroupManagerModal
          showGroupManager={showGroupManager}
          setShowGroupManager={setShowGroupManager}
          groups={groups}
          selectedGroupId={selectedGroupId}
          selectGroup={selectGroup}
          editingGroupId={editingGroupId}
          setEditingGroupId={setEditingGroupId}
          newGroupName={newGroupName}
          setNewGroupName={setNewGroupName}
          addGroup={addGroup}
          updateGroup={updateGroup}
          deleteGroup={deleteGroup}
          elements={elements}
        />
        <CreateGroupModal
          showCreateGroupModal={showCreateGroupModal}
          setShowCreateGroupModal={setShowCreateGroupModal}
          newGroupNameForModal={newGroupNameForModal}
          setNewGroupNameForModal={setNewGroupNameForModal}
          selectedElements={selectedElements}
          createGroupAndAssignElements={createGroupAndAssignElements}
        />
        {/* Legacy Modal Code - Remove after testing */}
        {false && showRightPanel && (
          <div
            className={`bg-white border border-gray-200 shadow-lg ${
              isRightPanelFloating
                ? "fixed z-50 rounded-lg"
                : "min-w-[240px] max-w-[400px] w-[20vw] flex-shrink-0 border-l border-t-0 border-b-0 border-r-0"
            }`}
            style={
              isRightPanelFloating
                ? {
                    left: `${Math.min(rightPanelPosition.x, window.innerWidth - 320)}px`,
                    top: `${Math.min(rightPanelPosition.y, window.innerHeight - 100)}px`,
                    width: `${Math.min(320, window.innerWidth * 0.3)}px`,
                    maxWidth: "90vw",
                    maxHeight: isRightPanelMinimized ? "auto" : "70vh",
                  }
                : {}
            }
            onMouseDown={
              isRightPanelFloating ? handleRightPanelMouseDown : undefined
            }
          >
            <div
              className={`${
                isRightPanelFloating
                  ? "right-panel-drag-handle cursor-move"
                  : ""
              } flex items-center justify-between px-3 py-2 border-b border-gray-300 bg-gray-100`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {isRightPanelFloating ? "Floating Panel" : "Properties"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {isRightPanelFloating && (
                  <>
                    <button
                      onClick={toggleRightPanelMinimize}
                      className="p-1 rounded hover:bg-gray-200 text-gray-600"
                      title={isRightPanelMinimized ? "Expand" : "Minimize"}
                    >
                      {isRightPanelMinimized ? (
                        <Plus className="w-3 h-3" />
                      ) : (
                        <Minus className="w-3 h-3" />
                      )}
                    </button>
                    <button
                      onClick={dockRightPanel}
                      className="p-1 rounded hover:bg-gray-200 text-gray-600"
                      title="Dock to Right"
                    >
                      <AlignRight className="w-3 h-3" />
                    </button>
                  </>
                )}
                {!isRightPanelFloating && (
                  <button
                    onClick={floatRightPanel}
                    className="p-1 rounded hover:bg-gray-200 text-gray-600"
                    title="Float Panel"
                  >
                    <Maximize2 className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={toggleRightPanel}
                  className="p-1 rounded hover:bg-gray-200 text-gray-600"
                  title="Close Panel"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
            {!isRightPanelMinimized && (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex border-b border-gray-300 bg-white flex-shrink-0">
                  <button
                    onClick={() => setRightPanel("layers")}
                    className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                      rightPanel === "layers"
                        ? "text-gray-900 bg-white border-b-2 border-gray-900"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" />
                      <span>Layers</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setRightPanel("objects")}
                    className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                      rightPanel === "objects"
                        ? "text-gray-900 bg-white border-b-2 border-gray-900"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <List className="w-3.5 h-3.5" />
                      <span>Objects</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setRightPanel("properties")}
                    className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                      rightPanel === "properties"
                        ? "text-gray-900 bg-white border-b-2 border-gray-900"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Settings className="w-3.5 h-3.5" />
                      <span>Properties</span>
                    </div>
                  </button>
                </div>
                <div
                  className={`p-4 overflow-y-auto flex-1 min-h-0 ${isRightPanelFloating ? "max-h-96" : ""}`}
                >
                  {rightPanel === "properties" && !selectedElement && (
                    <ScenarioPropertiesPanel
                      scenario={loadedScenario}
                      useCases={useCases}
                      assignments={loadedAssignments}
                      scenarioPatch={scenarioPatch}
                      setScenarioPatch={setScenarioPatch}
                      desiredAssignments={desiredAssignments}
                      setDesiredAssignments={setDesiredAssignments}
                    />
                  )}
                  {rightPanel === "properties" && selectedElement && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Properties
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ID
                        </label>
                        <input
                          type="text"
                          value={selectedElement.id}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Layer
                        </label>
                        <select
                          value={selectedElement.layer}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              layer: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          {Object.entries(LAYER_CONFIG).map(
                            ([layerId, config]) => (
                              <option key={layerId} value={layerId}>
                                {config.name}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={
                            selectedElement.label || selectedElement.text || ""
                          }
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              [selectedElement.type === "text"
                                ? "text"
                                : "label"]: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            X
                          </label>
                          <input
                            type="number"
                            value={selectedElement.x || selectedElement.cx || 0}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                [selectedElement.cx !== undefined ? "cx" : "x"]:
                                  parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            step="1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Y
                          </label>
                          <input
                            type="number"
                            value={selectedElement.y || selectedElement.cy || 0}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                [selectedElement.cy !== undefined ? "cy" : "y"]:
                                  parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            step="1"
                          />
                        </div>
                      </div>
                      {selectedElement.type === "rect" && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Width
                            </label>
                            <input
                              type="number"
                              value={selectedElement.width}
                              onChange={(e) =>
                                updateElement(selectedElement.id, {
                                  width: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Height
                            </label>
                            <input
                              type="number"
                              value={selectedElement.height}
                              onChange={(e) =>
                                updateElement(selectedElement.id, {
                                  height: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                      )}
                      {(selectedElement.type === "circle" ||
                        selectedElement.type === "tree") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Radius
                          </label>
                          <input
                            type="number"
                            value={selectedElement.r}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                r: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rotation (°)
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={selectedElement.rotation || 0}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                rotation: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            min="0"
                            max="360"
                            step="15"
                          />
                          <button
                            onClick={() =>
                              rotateElement(selectedElement.id, 90)
                            }
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Rotate 90°"
                          >
                            <RotateCw className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fill Color
                        </label>
                        <input
                          type="color"
                          value={selectedElement.fill}
                          onChange={(e) =>
                            updateElement(selectedElement.id, {
                              fill: e.target.value,
                            })
                          }
                          className="w-full h-10 border border-gray-300 rounded-md"
                        />
                      </div>
                      {selectedElement.type !== "text" && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Stroke Color
                            </label>
                            <input
                              type="color"
                              value={selectedElement.stroke}
                              onChange={(e) =>
                                updateElement(selectedElement.id, {
                                  stroke: e.target.value,
                                })
                              }
                              className="w-full h-10 border border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Stroke Width
                            </label>
                            <input
                              type="number"
                              value={selectedElement.strokeWidth}
                              onChange={(e) =>
                                updateElement(selectedElement.id, {
                                  strokeWidth: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              min="0"
                              step="0.5"
                            />
                          </div>
                        </>
                      )}
                      {selectedElement.type === "text" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Font Size
                          </label>
                          <input
                            type="number"
                            value={selectedElement.fontSize}
                            onChange={(e) =>
                              updateElement(selectedElement.id, {
                                fontSize: parseFloat(e.target.value) || 12,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            min="8"
                            max="72"
                          />
                        </div>
                      )}
                      {selectedElement.type === "pin" && (
                        <div className="space-y-4 border-t pt-4">
                          <h4 className="font-medium text-gray-900">
                            GPS Coordinates
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Latitude
                              </label>
                              <input
                                type="number"
                                step="0.000001"
                                value={selectedElement.gps?.latitude || ""}
                                onChange={(e) =>
                                  updatePinGPS(
                                    selectedElement.id,
                                    e.target.value,
                                    selectedElement.gps?.longitude || 0,
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="e.g. 52.520008"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Longitude
                              </label>
                              <input
                                type="number"
                                step="0.000001"
                                value={selectedElement.gps?.longitude || ""}
                                onChange={(e) =>
                                  updatePinGPS(
                                    selectedElement.id,
                                    selectedElement.gps?.latitude || 0,
                                    e.target.value,
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="e.g. 13.404954"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">
                              GPS Reference Point
                            </span>
                            <button
                              onClick={() =>
                                toggleReferencePoint(selectedElement.id)
                              }
                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                referencePoints.includes(selectedElement.id)
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : "bg-gray-100 text-gray-700 border border-gray-300"
                              }`}
                              disabled={!selectedElement.gps}
                            >
                              {referencePoints.includes(selectedElement.id)
                                ? "Reference Point"
                                : "Make Reference"}
                            </button>
                          </div>
                          {referencePoints.length >= 2 &&
                            scenarioMetadata.scale && (
                              <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                                GPS scaling active:{" "}
                                {scenarioMetadata.scale.toFixed(2)} meters per
                                pixel
                              </div>
                            )}
                          {selectedElement.gps &&
                            scenarioMetadata.coordinateSystem === "gps" && (
                              <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                                Calculated GPS:{" "}
                                {selectedElement.gps.latitude.toFixed(6)},{" "}
                                {selectedElement.gps.longitude.toFixed(6)}
                              </div>
                            )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-4 border-t">
                        <button
                          onClick={() => toggleElementLock(selectedElement.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                            selectedElement.locked
                              ? "bg-orange-100 text-orange-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {selectedElement.locked ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <Unlock className="w-4 h-4" />
                          )}
                          {selectedElement.locked ? "Locked" : "Unlocked"}
                        </button>
                        <button
                          onClick={() =>
                            toggleElementVisibility(selectedElement.id)
                          }
                          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                            selectedElement.visible
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {selectedElement.visible ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                          {selectedElement.visible ? "Visible" : "Hidden"}
                        </button>
                      </div>
                    </div>
                  )}
                  {rightPanel === "objects" && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Objects ({elements.length})
                      </h3>
                      <div className="space-y-1 max-h-96 overflow-y-auto">
                        {elements.map((element, index) => {
                          const isSel = selectedElements.find(
                            (el) => el.id === element.id,
                          );
                          let Icon;
                          if (element.type === "rect") Icon = Square;
                          else if (element.type === "circle") Icon = Circle;
                          else if (element.type === "text") Icon = Type;
                          else if (element.type === "pin") Icon = MapPin;
                          else if (element.type === "polygon") Icon = Pentagon;
                          else if (PARK_OBJECTS[element.type])
                            Icon = PARK_OBJECTS[element.type].icon;
                          else Icon = Square;
                          return (
                            <div
                              // eslint-disable-next-line react/no-array-index-key
                              key={index}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                isSel
                                  ? "bg-blue-100 border border-blue-300"
                                  : "hover:bg-gray-50"
                              }`}
                              onClick={() => setSelectedElements([element])}
                            >
                              <Icon className="w-4 h-4 text-gray-500" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {element.label ||
                                    element.text ||
                                    `${element.type}-${index + 1}`}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {LAYER_CONFIG[element.layer]?.name} •{" "}
                                  {PARK_OBJECTS[element.type]?.label ||
                                    element.type}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleElementVisibility(element.id);
                                  }}
                                  className="p-1 rounded hover:bg-gray-200"
                                >
                                  {element.visible ? (
                                    <Eye className="w-3 h-3 text-gray-500" />
                                  ) : (
                                    <EyeOff className="w-3 h-3 text-gray-400" />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleElementLock(element.id);
                                  }}
                                  className="p-1 rounded hover:bg-gray-200"
                                >
                                  {element.locked ? (
                                    <Lock className="w-3 h-3 text-orange-500" />
                                  ) : (
                                    <Unlock className="w-3 h-3 text-gray-500" />
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {rightPanel === "layers" && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Layers
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(LAYER_CONFIG)
                          .reverse()
                          .map(([layerId, config]) => {
                            const layerElements = getElementsForLayer(layerId);
                            const isActive = currentLayer === layerId;
                            const isVisible = layerVisibility[layerId];
                            return (
                              <div
                                key={layerId}
                                className={`p-3 border rounded-lg transition-colors ${
                                  isActive
                                    ? "border-blue-300 bg-blue-50"
                                    : "border-gray-200"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <button
                                    onClick={() => setCurrentLayer(layerId)}
                                    className="flex items-center gap-2 flex-1 text-left"
                                  >
                                    <config.icon className="w-4 h-4" />
                                    <span className="font-medium text-sm">
                                      {config.name}
                                    </span>
                                  </button>
                                  <button
                                    onClick={() =>
                                      toggleLayerVisibility(layerId)
                                    }
                                    className={`p-1 rounded transition-colors ${
                                      isVisible
                                        ? "text-gray-700 hover:bg-gray-100"
                                        : "text-gray-400 hover:bg-gray-100"
                                    }`}
                                  >
                                    {isVisible ? (
                                      <Eye className="w-4 h-4" />
                                    ) : (
                                      <EyeOff className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 mb-2">
                                  {config.description}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {layerElements.length} elements
                                </p>
                                {layerId === LAYERS.BACKGROUND && (
                                  <div className="mt-3 space-y-2">
                                    {backgroundImage && (
                                      <div className="text-xs text-green-600 bg-green-50 p-2 rounded mb-2">
                                        <div className="font-medium">
                                          Background Image Loaded
                                        </div>
                                        <div className="text-green-500">
                                          {backgroundImage.filename || "Image"}{" "}
                                          • {backgroundImage.originalWidth}×
                                          {backgroundImage.originalHeight}px
                                        </div>
                                        <div className="text-green-500">
                                          {backgroundImage.type.toUpperCase()} •{" "}
                                          {backgroundImage.source === "url"
                                            ? "URL"
                                            : "File"}{" "}
                                          • Fitted to ViewBox
                                        </div>
                                        {backgroundImage.source === "url" && (
                                          <div className="text-green-500 text-xs mt-1 break-all">
                                            {backgroundImage.url}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div className="space-y-2">
                                      <label className="block text-xs font-medium text-gray-700">
                                        Load Map Templates
                                      </label>
                                      <div className="grid grid-cols-1 gap-1">
                                        <button
                                          onClick={() => {
                                            const osmUrl =
                                              "https://tile.openstreetmap.org/15/17096/11273.png";
                                            importBackgroundFromURL(osmUrl);
                                          }}
                                          disabled
                                          title="Bald verfügbar"
                                          className="flex items-center gap-2 px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded border border-gray-200 cursor-not-allowed"
                                        >
                                          <Map className="w-3 h-3" />
                                          OpenStreetMap (Berlin)
                                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide">
                                            Bald
                                          </span>
                                        </button>
                                        <button
                                          onClick={() => {
                                            const satelliteUrl =
                                              "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/15/11273/17096";
                                            importBackgroundFromURL(
                                              satelliteUrl,
                                            );
                                          }}
                                          disabled
                                          title="Bald verfügbar"
                                          className="flex items-center gap-2 px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded border border-gray-200 cursor-not-allowed"
                                        >
                                          <Map className="w-3 h-3" />
                                          Satellite View (Berlin)
                                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide">
                                            Bald
                                          </span>
                                        </button>
                                        <button
                                          onClick={() => {
                                            const testUrl =
                                              "https://stadtplatz.de/hubfs/maps/svg_master_vhp2_export_plain.png";
                                            setBackgroundImageURL(testUrl);
                                            importBackgroundFromURL(testUrl);
                                          }}
                                          disabled
                                          title="Bald verfügbar"
                                          className="flex items-center gap-2 px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded border border-gray-200 cursor-not-allowed"
                                        >
                                          <Building className="w-3 h-3" />
                                          VHP Floor Plan
                                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide">
                                            Bald
                                          </span>
                                        </button>
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Weitere Quellen sind bald verfügbar –
                                        aktuell nur SVG-Upload.
                                      </div>
                                    </div>
                                    <div className="border-t pt-2">
                                      <label className="block text-xs font-medium text-gray-400">
                                        Custom Map Tile URL — bald verfügbar
                                      </label>
                                      <input
                                        type="text"
                                        value={backgroundImageURL}
                                        onChange={(e) =>
                                          setBackgroundImageURL(e.target.value)
                                        }
                                        disabled
                                        placeholder="https://example.com/tile/{z}/{x}/{y}.png"
                                        className="w-full px-2 py-1 text-xs border border-gray-200 bg-gray-50 text-gray-400 rounded mb-1 cursor-not-allowed"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="block text-xs font-medium text-gray-400">
                                        Load from Image URL — bald verfügbar
                                      </label>
                                      <div className="flex gap-1">
                                        <input
                                          type="url"
                                          value={backgroundImageURL}
                                          onChange={(e) =>
                                            setBackgroundImageURL(
                                              e.target.value,
                                            )
                                          }
                                          disabled
                                          placeholder="https://example.com/image.svg"
                                          className="flex-1 px-2 py-1 text-xs border border-gray-200 bg-gray-50 text-gray-400 rounded cursor-not-allowed"
                                        />
                                        <button
                                          onClick={() => {
                                            importBackgroundFromURL(
                                              backgroundImageURL,
                                            );
                                            setBackgroundImageURL("");
                                          }}
                                          disabled
                                          className="px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded cursor-not-allowed"
                                        >
                                          Load
                                        </button>
                                      </div>
                                    </div>
                                    <div className="border-t pt-2">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        SVG hochladen
                                      </label>
                                      <label className="cursor-pointer block">
                                        <input
                                          type="file"
                                          accept=".svg,image/svg+xml"
                                          onChange={importBackgroundImage}
                                          className="hidden"
                                          ref={backgroundInputRef}
                                        />
                                        <div className="flex items-center gap-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 border border-blue-300">
                                          <FileImage className="w-3 h-3" />
                                          {backgroundImage
                                            ? "SVG ersetzen"
                                            : "SVG hochladen"}
                                        </div>
                                      </label>
                                      <div className="text-xs text-gray-500 mt-1">
                                        Nur SVG-Dateien (Vektor) — keine
                                        Pixelbilder.
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-400 bg-gray-50 p-2 rounded space-y-1">
                                      <div>
                                        📍 Background Layer:{" "}
                                        {layerVisibility[LAYERS.BACKGROUND]
                                          ? "✅ Visible"
                                          : "❌ Hidden"}
                                      </div>
                                      <div>
                                        📐 ViewBox:{" "}
                                        {scenarioMetadata.viewBox.width}×
                                        {scenarioMetadata.viewBox.height}
                                      </div>
                                      {backgroundImage && (
                                        <>
                                          <div>
                                            🖼️ Loaded:{" "}
                                            {backgroundImage.originalWidth}×
                                            {backgroundImage.originalHeight}px
                                          </div>
                                          <div>
                                            📁 Source: {backgroundImage.source}{" "}
                                            (
                                            {backgroundImage.type.toUpperCase()}
                                            )
                                          </div>
                                          {backgroundImage.filename && (
                                            <div>
                                              📄 File:{" "}
                                              {backgroundImage.filename}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                    {backgroundImage && (
                                      <button
                                        onClick={() => {
                                          setBackgroundImage(null);
                                          setBackgroundImageURL("");
                                        }}
                                        className="flex items-center gap-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 w-full border border-red-300"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                        Remove Background
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Scenario Settings Modal */}
        {showScenarioSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Scenario Settings
                </h3>
                <button
                  onClick={() => setShowScenarioSettings(false)}
                  className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Scenario Name
                  </label>
                  <input
                    type="text"
                    value={scenarioMetadata.name}
                    onChange={(e) =>
                      setScenarioMetadata((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
                    placeholder="Enter scenario name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={scenarioMetadata.description}
                    onChange={(e) =>
                      setScenarioMetadata((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm h-20 dark:placeholder-slate-400"
                    placeholder="Describe your scenario"
                  />
                </div>
                <div className="border-t dark:border-slate-700 pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-slate-100 mb-3">
                    ViewBox Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                        X Offset
                      </label>
                      <input
                        type="number"
                        value={scenarioMetadata.viewBox.x}
                        onChange={(e) =>
                          setScenarioMetadata((prev) => ({
                            ...prev,
                            viewBox: {
                              ...prev.viewBox,
                              x: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                        Y Offset
                      </label>
                      <input
                        type="number"
                        value={scenarioMetadata.viewBox.y}
                        onChange={(e) =>
                          setScenarioMetadata((prev) => ({
                            ...prev,
                            viewBox: {
                              ...prev.viewBox,
                              y: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                        Width
                      </label>
                      <input
                        type="number"
                        value={scenarioMetadata.viewBox.width}
                        onChange={(e) =>
                          setScenarioMetadata((prev) => ({
                            ...prev,
                            viewBox: {
                              ...prev.viewBox,
                              width:
                                parseFloat(e.target.value) ||
                                DEFAULT_VIEWBOX.width,
                            },
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                        Height
                      </label>
                      <input
                        type="number"
                        value={scenarioMetadata.viewBox.height}
                        onChange={(e) =>
                          setScenarioMetadata((prev) => ({
                            ...prev,
                            viewBox: {
                              ...prev.viewBox,
                              height:
                                parseFloat(e.target.value) ||
                                DEFAULT_VIEWBOX.height,
                            },
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setScenarioMetadata((prev) => ({
                        ...prev,
                        viewBox: { ...DEFAULT_VIEWBOX },
                      }))
                    }
                    className="mt-2 px-3 py-1 text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                  >
                    Reset to Default
                  </button>
                </div>
                <div className="border-t dark:border-slate-700 pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-slate-100 mb-3">
                    Coordinate System & GPS Reference
                  </h4>
                  <div className="space-y-2 mb-4">
                    <div className="text-sm text-gray-700 dark:text-slate-300">
                      <label className="block mb-1 font-medium">
                        Coordinate System:
                      </label>
                      <select
                        value={scenarioMetadata.coordinateSystem}
                        onChange={(e) =>
                          setScenarioMetadata((prev) => ({
                            ...prev,
                            coordinateSystem: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                      >
                        <option value="svg">
                          SVG (Pixel-based x,y coordinates)
                        </option>
                        <option value="osm">
                          OSM (Geo-based lng,lat coordinates)
                        </option>
                      </select>
                    </div>
                    {scenarioMetadata.coordinateSystem === "osm" && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200 dark:border-blue-800">
                        <div className="font-medium mb-1">
                          OSM Mode Settings:
                        </div>
                        <div>
                          Center:{" "}
                          {scenarioMetadata.osmCenter
                            ? `${scenarioMetadata.osmCenter.lng.toFixed(6)}, ${scenarioMetadata.osmCenter.lat.toFixed(6)}`
                            : "Not set - Click on map or use GPS pins"}
                        </div>
                        <div>Zoom Level: {scenarioMetadata.osmZoom}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          In OSM mode, polygons store geographic coordinates
                          (lng/lat) instead of pixel coordinates.
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-2">
                    <span className="font-medium">
                      Current:{" "}
                      {scenarioMetadata.coordinateSystem === "svg"
                        ? "SVG Pixel Coordinates"
                        : "OSM Geo Coordinates"}
                    </span>
                  </div>
                  {referencePoints.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-slate-400 italic">
                      Add GPS coordinates to 2 pins and mark them as reference
                      points to enable GPS scaling.
                    </div>
                  )}
                  {referencePoints.map((refId) => {
                    const refPin = elements.find((el) => el.id === refId);
                    return refPin ? (
                      <div
                        key={refId}
                        className="flex items-center justify-between py-2 border-b dark:border-slate-700"
                      >
                        <div>
                          <div className="font-medium text-sm dark:text-slate-200">
                            {refPin.label}
                          </div>
                          {refPin.gps && (
                            <div className="text-xs text-gray-500 dark:text-slate-400">
                              {refPin.gps.latitude.toFixed(6)},{" "}
                              {refPin.gps.longitude.toFixed(6)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => toggleReferencePoint(refId)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : null;
                  })}
                  {scenarioMetadata.scale && (
                    <div className="mt-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded dark:border dark:border-green-800">
                      Scale: {scenarioMetadata.scale.toFixed(4)} meters per
                      pixel
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowScenarioSettings(false)}
                  className="px-4 py-2 text-gray-600 dark:text-slate-300 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Paste SVG Code Modal */}
        {showPasteSVGDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Paste SVG Code
                </h3>
                <button
                  onClick={() => {
                    setShowPasteSVGDialog(false);
                    setPasteSVGCode("");
                  }}
                  className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    {currentLayer ? (
                      <>
                        {React.createElement(LAYER_CONFIG[currentLayer].icon, {
                          className: "w-4 h-4",
                        })}
                        <span className="font-medium">
                          Target Layer: {LAYER_CONFIG[currentLayer].name}
                        </span>
                      </>
                    ) : (
                      <span className="font-medium text-red-600 dark:text-red-400">
                        ⚠️ No layer selected! Please select a layer first.
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    All imported elements will be added to this layer
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    SVG Code
                  </label>
                  <textarea
                    value={pasteSVGCode}
                    onChange={(e) => setPasteSVGCode(e.target.value)}
                    className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm font-mono dark:placeholder-slate-400"
                    placeholder={`Paste your SVG code here...\n\nExample:\n\n<svg>\n  <rect x="10" y="10" width="100" height="50" fill="blue" />\n  <circle cx="200" cy="50" r="30" fill="red" />\n  <text x="50" y="100" font-size="16">Hello World</text>\n</svg>`}
                    style={{ resize: "vertical" }}
                  />
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 dark:text-slate-100 mb-2">
                    Supported SVG Elements
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-slate-400">
                    <div>• &lt;rect&gt; → Rectangle</div>
                    <div>• &lt;circle&gt; → Circle</div>
                    <div>• &lt;ellipse&gt; → Circle (averaged)</div>
                    <div>• &lt;polygon&gt; → Polygon</div>
                    <div>• &lt;polyline&gt; → Polygon</div>
                    <div>• &lt;text&gt; → Text</div>
                    <div>• &lt;line&gt; → Thin Rectangle</div>
                    <div>• &lt;g&gt; → Group (flattened)</div>
                    <div>
                      • &lt;path&gt; → Text note (manual conversion needed)
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                    Note: Complex paths and advanced features may need manual
                    adjustment after import.
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 dark:text-slate-100">
                    Quick Examples
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() =>
                        setPasteSVGCode(`<svg>
                          <rect x="50" y="50" width="100" height="60" fill="#e3f2fd" stroke="#1976d2" stroke-width="2" />
                          <circle cx="200" cy="80" r="30" fill="#f3e5f5" stroke="#7b1fa2" stroke-width="2" />
                          <text x="120" y="140" font-size="14" fill="#333">Sample Elements</text>
                        </svg>`)
                      }
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Basic Shapes
                    </button>
                    <button
                      onClick={() =>
                        setPasteSVGCode(`<svg>
                                  <polygon points="100,50 150,25 200,50 175,100 125,100" fill="#fff3e0" stroke="#f57c00" stroke-width="2" />
                                  <polyline points="50,120 80,140 110,120 140,140 170,120" fill="none" stroke="#4caf50" stroke-width="3" />
                                </svg>`)
                      }
                      className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      Polygons
                    </button>
                    <button
                      onClick={() =>
                        setPasteSVGCode(`<svg>
                          <g transform="translate(50,50)">
                            <rect x="0" y="0" width="80" height="40" fill="#ffeb3b" stroke="#f57f17" stroke-width="2" />
                            <text x="40" y="25" text-anchor="middle" font-size="12" fill="#333">Building</text>
                          </g>
                        </svg>`)
                      }
                      className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                    >
                      Grouped Elements
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowPasteSVGDialog(false);
                    setPasteSVGCode("");
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-slate-300 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasteSVG}
                  disabled={!pasteSVGCode.trim() || !currentLayer}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                >
                  Import Elements
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Group Manager Modal */}
        {showGroupManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Manage Groups
                </h3>
                <button
                  onClick={() => {
                    setShowGroupManager(false);
                    setEditingGroupId(null);
                    setNewGroupName("");
                  }}
                  className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {/* Add New Group */}
                <div className="border-b dark:border-slate-700 pb-4">
                  <h4 className="font-medium text-gray-900 dark:text-slate-100 mb-2">
                    Add New Group
                  </h4>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Enter group name (e.g., scenario_new_large)"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && newGroupName.trim()) {
                          addGroup(newGroupName);
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (newGroupName.trim()) {
                          addGroup(newGroupName);
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Group List */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-slate-100 mb-2">
                    Existing Groups
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {groups
                      .filter((g) => g.id !== "layer1")
                      .map((group) => (
                        <div
                          key={group.id}
                          className={`flex items-center justify-between p-3 border rounded-lg ${
                            selectedGroupId === group.id
                              ? "border-blue-300 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                              : "border-gray-200 dark:border-slate-600"
                          }`}
                        >
                          {editingGroupId === group.id ? (
                            <div className="flex-1 flex gap-2">
                              <input
                                type="text"
                                defaultValue={group.name}
                                data-group-id={group.id}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    const input = e.target;
                                    updateGroup(group.id, input.value);
                                  }
                                }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  const input = document.querySelector(
                                    `input[data-group-id="${group.id}"]`,
                                  );
                                  if (input) {
                                    updateGroup(group.id, input.value);
                                  }
                                }}
                                className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-200 rounded text-sm hover:bg-green-200 dark:hover:bg-green-900/50"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingGroupId(null)}
                                className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex-1">
                                <div className="font-medium text-sm dark:text-slate-200">
                                  {group.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-slate-400">
                                  {
                                    elements.filter(
                                      (el) => el.groupId === group.id,
                                    ).length
                                  }{" "}
                                  polygons
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => selectGroup(group.id)}
                                  className={`px-3 py-1 rounded text-sm ${
                                    selectedGroupId === group.id
                                      ? "bg-blue-600 dark:bg-blue-500 text-white"
                                      : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                                  }`}
                                >
                                  {selectedGroupId === group.id
                                    ? "Selected"
                                    : "Select"}
                                </button>
                                <button
                                  onClick={() => setEditingGroupId(group.id)}
                                  className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200 rounded text-sm hover:bg-yellow-200 dark:hover:bg-yellow-900/50"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    if (
                                      // eslint-disable-next-line no-alert
                                      window.confirm(
                                        `Delete group "${group.name}"? All polygons in this group will be removed.`,
                                      )
                                    ) {
                                      deleteGroup(group.id);
                                    }
                                  }}
                                  className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded text-sm hover:bg-red-200 dark:hover:bg-red-900/50"
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    {groups.filter((g) => g.id !== "layer1").length === 0 && (
                      <div className="text-center text-gray-500 dark:text-slate-400 py-4">
                        No groups yet. Create one above.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowGroupManager(false);
                    setEditingGroupId(null);
                    setNewGroupName("");
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-slate-300 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Space Properties Modal */}
        <SpacePropertiesModal
          isOpen={showSpaceModal}
          onClose={closeSpaceModal}
          mode={spaceModalMode}
          element={
            spaceModalElementId
              ? elements.find((el) => el.id === spaceModalElementId)
              : null
          }
          updateSpaceMetadata={updateSpaceMetadata}
          updateSingleSpace={updateSingleSpace}
          createSingleSpace={createSingleSpace}
          deleteSingleSpace={async (elId) => {
            await deleteSingleSpace(elId);
            closeSpaceModal();
          }}
          savingSingleSpace={savingSingleSpace}
          deletingSingleSpace={deletingSingleSpace}
          useCases={useCases}
        />
        {/* Create Group Modal */}
        {showCreateGroupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Create Group
                </h3>
                <button
                  onClick={() => {
                    setShowCreateGroupModal(false);
                    setNewGroupNameForModal("");
                  }}
                  className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={newGroupNameForModal}
                    onChange={(e) => setNewGroupNameForModal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newGroupNameForModal.trim()) {
                        createGroupAndAssignElements();
                      } else if (e.key === "Escape") {
                        setShowCreateGroupModal(false);
                        setNewGroupNameForModal("");
                      }
                    }}
                    placeholder="Enter group name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:placeholder-slate-400"
                    autoFocus
                  />
                </div>
                <div className="text-sm text-gray-500 dark:text-slate-400">
                  {selectedElements.length} element
                  {selectedElements.length !== 1 ? "s" : ""} will be added to
                  this group.
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowCreateGroupModal(false);
                    setNewGroupNameForModal("");
                  }}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-700 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={createGroupAndAssignElements}
                  disabled={!newGroupNameForModal.trim()}
                  className="px-4 py-2 text-sm text-white bg-blue-600 dark:bg-blue-500 rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                >
                  Create Group
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
